import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Tooltip,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  Collapse,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  MedicalServices as ServiceIcon,
  Link as LinkIcon,
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon,
  Save as SaveIcon,
  Psychology as PsychologyIcon,
  Science as ScienceIcon,
  Archive as ArchiveIcon,
  Sync as SyncIcon,
  InfoOutlined as InfoOutlinedIcon,
  RecordVoiceOver as VoiceIcon,
  SmartToy as AutomationIcon,
  ForumOutlined as PleasantriesIcon,
  Tune as TuneIcon,
  SmsOutlined as SmsIcon,
  EventAvailableOutlined as BookingLinkIcon,
  HealthAndSafetyOutlined as WellnessIcon,
  Inbox as IntakeIcon,
  Description as FaxTabIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { specialistsApi, servicesApi, serviceMappingApi, settingsApi, contactSyncApi, brandingApi, aiTrainingApi, labDirectoryApi, aiVoiceApi, aiSkillsApi, proxyIntakeApi, faxSignaturesApi, faxFilingsApi, aiSchedulingConfigApi } from '../services/api';
import { setPracticeTimeZone, listPracticeTimeZones } from '../utils/practiceTime';
import SignaturePad, { SignaturePadHandle } from '../components/common/SignaturePad';
import { useBrandingStore } from '../store/brandingStore';
import { LabDirectoryEntry } from '../types';
import MobileRecordCard from '../components/common/MobileRecordCard';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { templateProblems, insertTag } from './messageTemplate';
import { heldOfferText, STANDARD_OFFER_TEMPLATE } from '../components/Dashboard/offerMessage';
import { renderOutreach, STANDARD_OUTREACH_TEMPLATE } from '../components/Dashboard/wellnessOutreach';
import { linkLifetimePhrase, holdDays } from '../components/Dashboard/heldOfferMessage';
import { confirmationText, STANDARD_CONFIRMATION_TEMPLATE } from '../components/Dashboard/confirmationMessage';

const VOICE_LIBRARY_CARDS: Array<{ key: string; title: string; hint: string }> = [
  { key: 'concise_warm', title: '1 · Concise & Warm', hint: 'Friendly, 2–4 sentences.' },
  { key: 'brief_clinical', title: '2 · Brief & Clinical', hint: 'Direct, 1–3 sentences.' },
  { key: 'detailed', title: '3 · More Detailed', hint: 'Explain the why — still no filler.' },
  { key: 'custom', title: '4 · Custom', hint: 'Used when Custom is selected above.' },
];

// Types
interface TimeBlock {
  start: string;
  end: string;
}

interface WorkingHours {
  monday: TimeBlock[];
  tuesday: TimeBlock[];
  wednesday: TimeBlock[];
  thursday: TimeBlock[];
  friday: TimeBlock[];
  saturday: TimeBlock[];
  sunday: TimeBlock[];
}

interface Specialist {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  isActive: boolean;
  workingDays: string[];
  workingHours: WorkingHours;
}

interface Service {
  id: string;
  name: string;
  duration: number;
  isActive: boolean;
}

interface ServiceMapping {
  id: string;
  serviceId: string;
  specialistId: string;
  service: Service;
  specialist: Specialist;
  /** Minutes this provider books this service for. Null means inherit the service. */
  duration?: number | null;
}

type TabValue = 'providers' | 'services' | 'mappings' | 'settings' | 'branding' | 'aitraining' | 'intake' | 'faxtosign' | 'labs';

interface AppSettings {
  sms_confirmation_enabled: boolean;
  sms_reminder_enabled: boolean;
  sms_reminder_hours: number;
  sms_cancellation_enabled: boolean;
  scheduling_slot_duration: number;
  scheduling_buffer_minutes: number;
  scheduling_same_day_allowed: boolean;
  practice_timezone: string;
  scheduling_wellness_link_ttl_days: number;
  scheduling_wellness_spread_cap: number;
  wellness_outreach_message: string;
  scheduling_link_ttl_days: number;
  scheduling_offer_message: string;
  practice_phone: string;
  scheduling_confirmation_message: string;
  working_hours_start: string;
  working_hours_end: string;
  working_days: string[];
  contact_sync_enabled: boolean;
  contact_sync_interval_minutes: number;
  // AI Automation Settings
  appointment_triggers_enabled: boolean;
  pleasantries_auto_response_enabled: boolean;
}

interface SmsTemplate {
  name: string;
  template: string;
  variables: string[];
}

const DEFAULT_WORKING_HOURS: WorkingHours = {
  monday: [{ start: '09:00', end: '12:00' }, { start: '13:00', end: '17:00' }],
  tuesday: [{ start: '09:00', end: '12:00' }, { start: '13:00', end: '17:00' }],
  wednesday: [{ start: '09:00', end: '12:00' }, { start: '13:00', end: '17:00' }],
  thursday: [{ start: '09:00', end: '12:00' }, { start: '13:00', end: '17:00' }],
  friday: [{ start: '09:00', end: '12:00' }, { start: '13:00', end: '17:00' }],
  saturday: [],
  sunday: [],
};

// The previews in Settings fill a message in for a made-up patient. The day is
// a few days out and the link is the right length, so what staff read is the
// shape and size of the real thing.
const OFFER_TAG_BUTTONS = [
  { name: 'firstName', label: 'First name' },
  { name: 'service', label: 'Service' },
  { name: 'provider', label: 'Provider' },
  { name: 'times', label: 'Times' },
  { name: 'link', label: 'Link' },
  { name: 'heldUntil', label: 'Held until' },
];
const CONFIRMATION_TAG_BUTTONS = [
  { name: 'firstName', label: 'First name' },
  { name: 'service', label: 'Service' },
  { name: 'provider', label: 'Provider' },
  { name: 'when', label: 'When' },
  { name: 'phone', label: 'Practice phone' },
];
const OUTREACH_TAG_BUTTONS = [
  { name: 'firstName', label: 'First name' },
  { name: 'provider', label: 'Provider' },
  { name: 'link', label: 'Link' },
  { name: 'expires', label: 'Expires' },
];
const sampleLink = (): string => `${window.location.origin}/b/Xk3mP9qRt2Wz`;
const sampleDay = (): string => {
  const d = new Date();
  d.setDate(d.getDate() + 3);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// Progressive disclosure for card explainers: the teaching copy lives behind a
// small info icon (hover on desktop, tap on mobile) instead of permanent
// paragraphs/alerts, so trained staff see controls, not lessons.
const InfoTip = ({ text }: { text: string }) => (
  <Tooltip title={text} arrow enterTouchDelay={0} leaveTouchDelay={5000}>
    <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled', cursor: 'help' }} />
  </Tooltip>
);

// The Settings tab is a grid of cards that sit two to a row, so they share one
// shell: same header, same padding, and height:100% — without it a short card
// leaves a ragged void beside a tall one, which is what this tab looked like.
// The body grows, so footer buttons line up across neighbours.
const SettingSection = ({
  icon,
  title,
  help,
  chip,
  action,
  accent,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  help?: string;
  chip?: React.ReactNode;
  action?: React.ReactNode;
  accent?: 'warning' | 'error';
  children: React.ReactNode;
}) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      borderColor: accent ? `${accent}.main` : 'divider',
      borderWidth: accent ? 2 : 1,
    }}
  >
    <CardContent
      sx={{
        p: { xs: 1.75, sm: 2 },
        '&:last-child': { pb: { xs: 1.75, sm: 2 } },
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        gap: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 32 }}>
        {icon}
        <Typography
          variant="h6"
          sx={{ fontSize: { xs: '1rem', sm: '1.0625rem' }, fontWeight: 600, lineHeight: 1.3 }}
        >
          {title}
        </Typography>
        {help && <InfoTip text={help} />}
        {chip}
        <Box sx={{ flexGrow: 1 }} />
        {action}
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>{children}</Box>
    </CardContent>
  </Card>
);

// Label and explanation on the left, control on the right. Every switch in the
// tab then lines up in one column, instead of each caption hanging off a magic
// ml:6 indent that also ate half the width on a phone.
const SettingRow = ({
  label,
  help,
  tip,
  control,
  divider = true,
  stack = false,
}: {
  label: string;
  help?: string;
  /** The longer explanation, behind an info icon: what the setting touches,
   *  an example. The help line says what it is; this says what follows. */
  tip?: string;
  control: React.ReactNode;
  divider?: boolean;
  /** Put the control under the label on a phone — for text and select fields,
   *  which get squeezed to nothing when they share a narrow row. */
  stack?: boolean;
}) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: stack ? { xs: 'column', sm: 'row' } : 'row',
      alignItems: stack ? { xs: 'stretch', sm: 'center' } : 'center',
      justifyContent: 'space-between',
      gap: stack ? { xs: 1, sm: 2 } : 2,
      py: 1.25,
      ...(divider ? { borderBottom: '1px solid', borderColor: 'divider' } : {}),
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
          {label}
        </Typography>
        {tip && <InfoTip text={tip} />}
      </Box>
      {help && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 0.25, lineHeight: 1.45 }}
        >
          {help}
        </Typography>
      )}
    </Box>
    <Box
      sx={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        ...(stack ? { width: { xs: '100%', sm: 'auto' } } : {}),
      }}
    >
      {control}
    </Box>
  </Box>
);

// A patient message the practice writes itself. The tags are buttons, so
// nobody has to type a brace or remember a spelling; what would go wrong on a
// patient's phone (no link, a tag the app does not know) is said here, while
// it is being written; and the preview is the real message, filled in by the
// same code that fills it for a patient.
const MessageSetting = ({
  label,
  help,
  tip,
  value,
  onChange,
  tags,
  standard,
  preview,
  divider = true,
  requireLink = true,
}: {
  label: string;
  help: string;
  tip?: string;
  /** false for a message sent after booking: there is no link to carry. */
  requireLink?: boolean;
  value: string;
  onChange: (next: string) => void;
  tags: { name: string; label: string }[];
  /** The wording used when the box is empty, written with its tags. */
  standard: string;
  /** The message as a sample patient would read it. */
  preview: (template: string) => string;
  divider?: boolean;
}) => {
  const boxRef = useRef<HTMLTextAreaElement | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  // Folded to one row until somebody wants to change it: three message
  // editors open at once made the Booking Links card twice the height of its
  // neighbour, which was then stretched to match. A message with something
  // wrong in it opens itself, so a warning is never folded out of sight.
  const [open, setOpen] = useState(false);
  const text = String(value || '');
  const blank = !text.trim();
  const problems = templateProblems(text, tags.map((t) => t.name), { requireLink });

  const addTag = (name: string) => {
    const el = boxRef.current;
    const next = insertTag(text, el?.selectionStart, el?.selectionEnd, name);
    onChange(next.text);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(next.caret, next.caret);
    });
  };

  return (
    <Box sx={{ py: 1.25, ...(divider ? { borderBottom: '1px solid', borderColor: 'divider' } : {}) }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
          {label}
        </Typography>
        {tip && <InfoTip text={tip} />}
        <Box sx={{ flexGrow: 1 }} />
        <Chip
          label={blank ? 'Standard wording' : 'Your wording'}
          size="small"
          variant="outlined"
          color={blank ? 'default' : 'primary'}
        />
        <Button
          size="small"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open || problems.length > 0}
          aria-label={`${open || problems.length > 0 ? 'Close' : 'Edit'} ${label}`}
          sx={{ textTransform: 'none', minWidth: 0, ml: 0.25 }}
        >
          {open || problems.length > 0 ? 'Done' : 'Edit'}
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>
        {help}
      </Typography>
      <Collapse in={open || problems.length > 0} unmountOnExit>
      <Box sx={{ pt: 1 }}>
      <TextField
        multiline
        minRows={4}
        fullWidth
        value={text}
        onChange={(e) => onChange(e.target.value)}
        placeholder={standard}
        size="small"
        inputRef={boxRef}
        sx={{ '& textarea': { fontSize: '0.85rem', lineHeight: 1.5 } }}
        inputProps={{ 'aria-label': label }}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mt: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
          Insert
        </Typography>
        {tags.map((t) => (
          <Chip
            key={t.name}
            label={t.label}
            size="small"
            variant="outlined"
            onClick={() => addTag(t.name)}
            aria-label={`Insert ${t.label}`}
          />
        ))}
      </Box>
      {problems.map((problem) => (
        <Alert key={problem} severity="warning" sx={{ mt: 1, py: 0, '& .MuiAlert-message': { fontSize: '0.8125rem' } }}>
          {problem}
        </Alert>
      ))}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, ml: -0.75 }}>
        <Button size="small" onClick={() => setShowPreview((v) => !v)} aria-expanded={showPreview}>
          {showPreview ? 'Hide preview' : 'Preview'}
        </Button>
        <Button size="small" color="inherit" onClick={() => onChange(blank ? standard : '')} sx={{ color: 'text.secondary' }}>
          {blank ? 'Edit the standard wording' : 'Back to standard wording'}
        </Button>
      </Box>
      <Collapse in={showPreview} unmountOnExit>
        <Box sx={{ mt: 0.5, p: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            As a patient would read it — sample name and times
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.5 }}>
            {preview(text)}
          </Typography>
        </Box>
      </Collapse>
      </Box>
      </Collapse>
    </Box>
  );
};

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday', short: 'Mon' },
  { key: 'tuesday', label: 'Tuesday', short: 'Tue' },
  { key: 'wednesday', label: 'Wednesday', short: 'Wed' },
  { key: 'thursday', label: 'Thursday', short: 'Thu' },
  { key: 'friday', label: 'Friday', short: 'Fri' },
  { key: 'saturday', label: 'Saturday', short: 'Sat' },
  { key: 'sunday', label: 'Sunday', short: 'Sun' },
];

const defaultSettings: AppSettings = {
  sms_confirmation_enabled: true,
  sms_reminder_enabled: true,
  sms_reminder_hours: 24,
  sms_cancellation_enabled: true,
  scheduling_slot_duration: 30,
  scheduling_buffer_minutes: 0,
  scheduling_same_day_allowed: true,
  practice_timezone: 'America/Chicago',
  scheduling_wellness_link_ttl_days: 30,
  scheduling_wellness_spread_cap: 20,
  wellness_outreach_message: '',
  scheduling_link_ttl_days: 1,
  scheduling_offer_message: '',
  practice_phone: '',
  scheduling_confirmation_message: '',
  working_hours_start: '09:00',
  working_hours_end: '17:00',
  working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
  contact_sync_enabled: true,
  contact_sync_interval_minutes: 5,
  // AI Automation - default to FALSE for safety
  appointment_triggers_enabled: false,
  pleasantries_auto_response_enabled: false,
};

// Helper to format time blocks for display
const formatTimeBlocks = (blocks: TimeBlock[]): string => {
  if (!blocks || blocks.length === 0) return 'Not working';
  return blocks.map(b => `${b.start} - ${b.end}`).join(', ');
};

// Compact a single "HH:MM" into "8", "8:30" — drops leading zero and :00.
const compactTime = (t: string): string => {
  if (!t) return '';
  const [hRaw, mRaw] = t.split(':');
  const h = String(parseInt(hRaw, 10));
  return mRaw && mRaw !== '00' ? `${h}:${mRaw}` : h;
};

// Outer working span for a day (earliest start → latest end) plus whether
// there's a break (more than one block). e.g. [10-12,13-16] -> "10–16" (break).
const formatDaySpan = (blocks: TimeBlock[]): { span: string; hasBreak: boolean } => {
  if (!blocks || blocks.length === 0) return { span: '', hasBreak: false };
  const start = compactTime(blocks[0].start);
  const end = compactTime(blocks[blocks.length - 1].end);
  return { span: `${start}–${end}`, hasBreak: blocks.length > 1 };
};

const parseCommaList = (value: string) =>
  value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

// What this pairing actually books. A provider can override the service's
// duration (Dr. Levitan's consultations run 90 minutes where the service says
// 60), and showing the service default here reported the wrong number for
// exactly the pairings someone came to this tab to check.
const describeMappingDuration = (mapping: any): string => {
  const serviceDuration = mapping?.service?.duration;
  const override = mapping?.duration;
  if (override && override !== serviceDuration) {
    return `${override} mins (overrides ${serviceDuration})`;
  }
  return `${override || serviceDuration} mins`;
};

const Admin: React.FC = () => {
  // App chrome + table->card switches key off `down('md')` (see plan).
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState<TabValue>('aitraining');
  const [specialists, setSpecialists] = useState<Specialist[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [mappings, setMappings] = useState<ServiceMapping[]>([]);
  const [labs, setLabs] = useState<LabDirectoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Settings state
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [patientSelfBooking, setPatientSelfBooking] = useState(false);

  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // AI Voice — practice default + module overrides + Voice Skill Library
  const [aiVoicePresets, setAiVoicePresets] = useState<Array<{ key: string; label: string; description: string; text: string }>>([]);
  const [aiVoiceModules, setAiVoiceModules] = useState<Array<{ key: string; label: string; description: string }>>([]);
  const [aiVoicePreset, setAiVoicePreset] = useState<string>('concise_warm');
  const [aiVoiceEffective, setAiVoiceEffective] = useState<string>('');
  const [aiVoiceModulePick, setAiVoiceModulePick] = useState<Record<string, string>>({
    triage: 'default',
    consultation_doctor: 'default',
    consultation_patient: 'default',
  });
  const [aiVoiceSaving, setAiVoiceSaving] = useState(false);
  const [aiVoiceLibrary, setAiVoiceLibrary] = useState<Record<string, string>>({
    concise_warm: '',
    brief_clinical: '',
    detailed: '',
    custom: '',
  });
  const [aiVoiceLibrarySaving, setAiVoiceLibrarySaving] = useState(false);
  const [aiVoiceLibrarySavedAt, setAiVoiceLibrarySavedAt] = useState<number | null>(null);
  const aiVoiceLibraryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const aiVoiceLibrarySkipDebounceRef = useRef(true);
  const aiVoiceLibrarySeqRef = useRef(0);
  const aiVoiceLibraryLatestRef = useRef(aiVoiceLibrary);
  aiVoiceLibraryLatestRef.current = aiVoiceLibrary;

  const loadAiVoice = useCallback(async () => {
    try {
      const res = await aiVoiceApi.get();
      const d = res.data || {};
      setAiVoicePresets(d.presets || []);
      setAiVoiceModules(d.modules || [
        { key: 'triage', label: 'Triage Dashboard', description: '' },
        { key: 'consultation_doctor', label: 'AI Consultation (Doctor)', description: '' },
        { key: 'consultation_patient', label: 'AI Consultation (Patient Message)', description: '' },
      ]);
      const def = d.selected?.default || d.selected || {};
      setAiVoicePreset(def.preset || 'concise_warm');
      setAiVoiceEffective(d.effectiveText || d.effectiveByModule?.default || '');
      const lib = d.library || d.selected?.library || {};
      aiVoiceLibrarySkipDebounceRef.current = true;
      setAiVoiceLibrary({
        concise_warm: typeof lib.concise_warm === 'string' ? lib.concise_warm : '',
        brief_clinical: typeof lib.brief_clinical === 'string' ? lib.brief_clinical : '',
        detailed: typeof lib.detailed === 'string' ? lib.detailed : '',
        custom: typeof lib.custom === 'string' ? lib.custom : (def.customText || ''),
      });
      const picks: Record<string, string> = {
        triage: 'default',
        consultation_doctor: 'default',
        consultation_patient: 'default',
      };
      const mods = d.selected?.modules || {};
      for (const key of Object.keys(picks)) {
        picks[key] = mods[key]?.preset ? String(mods[key].preset) : 'default';
      }
      // Legacy single consultation override
      if (picks.consultation_doctor === 'default' && mods.consultation?.preset) {
        picks.consultation_doctor = String(mods.consultation.preset);
      }
      if (picks.consultation_patient === 'default' && mods.consultation?.preset) {
        picks.consultation_patient = String(mods.consultation.preset);
      }
      setAiVoiceModulePick(picks);
    } catch (e) {
      // Non-fatal: the card just shows defaults.
    }
  }, []);

  const handleSaveAiVoice = async () => {
    setAiVoiceSaving(true);
    try {
      const customText = aiVoiceLibrary.custom || '';
      const modules: Record<string, { preset: string; customText?: string } | null> = {};
      for (const [key, pick] of Object.entries(aiVoiceModulePick)) {
        modules[key] = pick === 'default'
          ? null
          : { preset: pick, customText: pick === 'custom' ? customText : '' };
      }
      const res = await aiVoiceApi.update({
        default: { preset: aiVoicePreset, customText },
        modules,
        library: aiVoiceLibrary,
      });
      setAiVoiceEffective(res.data?.effectiveText || '');
      // Keep local library text as-is after explicit save (avoid caret jump if response normalizes whitespace).
      const sync = res.data?.chatbaseSync;
      if (sync?.ok) {
        const bits = [];
        if (sync.triage?.changed) bits.push('triage');
        if (sync.consult?.changed) bits.push('consult');
        setSuccessMessage(
          bits.length
            ? `AI voice saved — Chatbase updated (${bits.join(' + ')}).`
            : 'AI voice saved — Chatbase already in sync.'
        );
      } else if (sync && sync.ok === false) {
        setSuccessMessage('AI voice saved locally. Chatbase sync failed — check logs or retry Save.');
      } else {
        setSuccessMessage('AI voice saved — new drafts will use it right away.');
      }
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (e: any) {
      setError(e?.response?.status === 403
        ? 'Only an admin can change the AI voice.'
        : 'Failed to save the AI voice.');
    } finally {
      setAiVoiceSaving(false);
    }
  };

  const persistAiVoiceLibrary = useCallback(async (library: Record<string, string>, seq: number) => {
    setAiVoiceLibrarySaving(true);
    try {
      // Local persist only (Chatbase sync runs in background on the server for library-only PUTs).
      // Do NOT write res.library back into the textareas — that steals focus / jumps the caret
      // when a slower response lands after the user has already typed more.
      await aiVoiceApi.update({ library });
      if (seq === aiVoiceLibrarySeqRef.current) {
        setAiVoiceLibrarySavedAt(Date.now());
      }
    } catch (e: any) {
      // Quiet error for autosave — avoid layout-shifting banners while typing.
      console.error('Voice Skill Library autosave failed', e);
      if (seq === aiVoiceLibrarySeqRef.current) {
        setAiVoiceLibrarySavedAt(null);
      }
    } finally {
      if (seq === aiVoiceLibrarySeqRef.current) {
        setAiVoiceLibrarySaving(false);
      }
    }
  }, []);

  const handleAiVoiceLibraryChange = (key: string, value: string) => {
    setAiVoiceLibrary((prev) => {
      const next = { ...prev, [key]: value };
      aiVoiceLibraryLatestRef.current = next;
      return next;
    });
    aiVoiceLibrarySeqRef.current += 1;
    const seq = aiVoiceLibrarySeqRef.current;
    if (aiVoiceLibrarySkipDebounceRef.current) {
      aiVoiceLibrarySkipDebounceRef.current = false;
    }
    if (aiVoiceLibraryTimerRef.current) clearTimeout(aiVoiceLibraryTimerRef.current);
    aiVoiceLibraryTimerRef.current = setTimeout(() => {
      persistAiVoiceLibrary(aiVoiceLibraryLatestRef.current, seq);
    }, 900);
  };
  
  // SMS Templates modal
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [smsTemplates, setSmsTemplates] = useState<Record<string, SmsTemplate>>({});
  
  // Branding state
  const { branding, fetchBranding, updateBranding } = useBrandingStore();
  const [brandingForm, setBrandingForm] = useState({
    headerText: '',
    sloganText: '',
    copyrightText: '',
  });
  const [isSavingBranding, setIsSavingBranding] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  
  // AI Training states (clinic knowledge docs — not voice tone)
  const [isUploadingTraining, setIsUploadingTraining] = useState(false);

  // Compression skills (skill store)
  type AiSkillRow = {
    key: string;
    name: string;
    taskType: string;
    description?: string;
    aggressiveness: string;
    enabled: boolean;
    engine: string;
  };
  const [aiSkills, setAiSkills] = useState<AiSkillRow[]>([]);
  const [aiSkillsSaving, setAiSkillsSaving] = useState<string | null>(null);

  // Proxy SMS intake
  const [proxyIntakeEnabled, setProxyIntakeEnabled] = useState(false);
  const [proxyPhonesText, setProxyPhonesText] = useState('');
  const [proxyContactIdsText, setProxyContactIdsText] = useState('');
  const [proxyIntakeSaving, setProxyIntakeSaving] = useState(false);
  const [proxyPreviewText, setProxyPreviewText] = useState('Abbie Szot\nneeds a refill of esomeprazole');
  const [proxyPreviewResult, setProxyPreviewResult] = useState<any>(null);

  // Faxes-to-sign allowlist
  const [faxSignEnabled, setFaxSignEnabled] = useState(false);
  const [faxSignPhonesText, setFaxSignPhonesText] = useState('');
  const [faxAutoSendPhonesText, setFaxAutoSendPhonesText] = useState('');
  const [faxSignCues, setFaxSignCues] = useState(true);
  const [faxSignSaving, setFaxSignSaving] = useState(false);
  const [faxAutoSendEnabled, setFaxAutoSendEnabled] = useState(false);
  const [faxSuggestedPhones, setFaxSuggestedPhones] = useState<string[]>([]);
  const [faxPracticeSigUrl, setFaxPracticeSigUrl] = useState<string | null>(null);
  const [faxFiledRotationDays, setFaxFiledRotationDays] = useState(7);
  const [faxFiledSaving, setFaxFiledSaving] = useState(false);
  const faxSigPadRef = useRef<SignaturePadHandle | null>(null);
  const [faxSigPadHasInk, setFaxSigPadHasInk] = useState(false);
  const [faxSigPadSaving, setFaxSigPadSaving] = useState(false);
  const [trainingMessage, setTrainingMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Modal states
  const [isSpecialistModalOpen, setIsSpecialistModalOpen] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isMappingModalOpen, setIsMappingModalOpen] = useState(false);
  const [isLabModalOpen, setIsLabModalOpen] = useState(false);
  const [editingSpecialist, setEditingSpecialist] = useState<Specialist | null>(null);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [editingLab, setEditingLab] = useState<LabDirectoryEntry | null>(null);

  // Enhanced form states for specialist
  const [specialistForm, setSpecialistForm] = useState<{
    name: string;
    phone: string;
    email: string;
    isActive: boolean;
    workingHours: WorkingHours;
  }>({
    name: '',
    phone: '',
    email: '',
    isActive: true,
    workingHours: DEFAULT_WORKING_HOURS,
  });

  const [serviceForm, setServiceForm] = useState({
    name: '',
    duration: 30,
    isActive: true,
  });
  const [mappingForm, setMappingForm] = useState({
    serviceId: '',
    specialistId: '',
  });
  const [labForm, setLabForm] = useState({
    name: '',
    category: '',
    aliases: '',
    phoneNumbers: '',
    notes: '',
    status: 'active',
  });
  const [isSavingLab, setIsSavingLab] = useState(false);

  // Fetch data
  useEffect(() => {
    fetchData();
    fetchSettings();
    fetchBranding();
    loadAiVoice();
  }, [loadAiVoice]);

  // Initialize branding form when branding data loads
  useEffect(() => {
    setBrandingForm({
      headerText: branding.headerText,
      sloganText: branding.sloganText,
      copyrightText: branding.copyrightText,
    });
    if (branding.logoUrl) {
      setLogoPreview(branding.logoUrl);
    }
  }, [branding]);

  useEffect(() => {
    if (activeTab === 'aitraining') {
      loadAiSkills();
    }
    if (activeTab === 'intake') {
      loadProxyIntake();
    }
    if (activeTab === 'faxtosign') {
      loadFaxSignatures();
    }
  }, [activeTab]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [specialistsRes, servicesRes, mappingsRes, labsRes] = await Promise.all([
        specialistsApi.getAll(),
        servicesApi.getAll(),
        serviceMappingApi.getAll(),
        labDirectoryApi.list(true),
      ]);
      // API returns arrays directly, not wrapped in { specialists: [...] }
      const specialistsData = specialistsRes.data?.specialists || specialistsRes.data || [];
      setSpecialists(Array.isArray(specialistsData) ? specialistsData : []);
      
      const servicesData = servicesRes.data?.services || servicesRes.data || [];
      setServices(Array.isArray(servicesData) ? servicesData : []);
      
      const mappingsData = mappingsRes.data?.mappings || mappingsRes.data || [];
      setMappings(Array.isArray(mappingsData) ? mappingsData : []);

      const labsData = labsRes.data?.data || labsRes.data || [];
      setLabs(Array.isArray(labsData) ? labsData : []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await settingsApi.getAll();
      const settingsData = response.data?.settings || {};
      
      const parsed: Partial<AppSettings> = {};
      Object.entries(settingsData).forEach(([key, data]: [string, any]) => {
        parsed[key as keyof AppSettings] = data.value;
      });
      
      setSettings(prev => ({ ...prev, ...parsed }));
      if (typeof (parsed as { practice_timezone?: string }).practice_timezone === 'string') {
        setPracticeTimeZone((parsed as { practice_timezone?: string }).practice_timezone);
      }
      try {
        const cfg = await aiSchedulingConfigApi.get();
        setPatientSelfBooking(Boolean(cfg.data?.data?.enablePatientSelfBooking));
      } catch (cfgErr) {
        setPatientSelfBooking(false);
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  };

  const handleSaveSettings = async () => {
    setIsSavingSettings(true);
    try {
      await settingsApi.bulkUpdate(settings);
      await aiSchedulingConfigApi.update({ enablePatientSelfBooking: patientSelfBooking });
      setPracticeTimeZone(settings.practice_timezone);
      setSuccessMessage('Settings saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError('Failed to save settings');
    } finally {
      setIsSavingSettings(false);
    }
  };

  // Destructive actions ask through a dialog this app renders, not
  // window.confirm. A browser that declines to show the native one returns
  // false, which reads as "the user said no" — so the button did nothing at
  // all, silently. Same fix as the calendar's cancel buttons.
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    body: React.ReactNode;
    confirmLabel: string;
    run: () => Promise<void>;
  } | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const askConfirm = (ask: {
    title: string;
    body: React.ReactNode;
    confirmLabel: string;
    run: () => Promise<void>;
  }) => setPendingConfirm(ask);

  const runPendingConfirm = async () => {
    if (!pendingConfirm) return;
    setConfirmBusy(true);
    try {
      await pendingConfirm.run();
      setPendingConfirm(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  const handleResetSettings = async () => {
    askConfirm({
      title: 'Reset all settings to defaults?',
      body: 'Every setting on this page goes back to its default, including the SMS toggles and the scheduling buffer. Settings on other tabs are not affected.',
      confirmLabel: 'Reset to defaults',
      run: async () => {
    try {
      await settingsApi.reset();
      setSettings(defaultSettings);
      setPracticeTimeZone(defaultSettings.practice_timezone);
      setSuccessMessage('Settings reset to defaults!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to reset settings:', err);
      setError('Failed to reset settings');
    }
      },
    });
  };

  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncPreview, setSyncPreview] = useState<any>(null);
  const [isArchivingDetag, setIsArchivingDetag] = useState(false);

  // Smart Spruce sync: pull LevitanMD-tagged contacts (add/refresh active), then surface any
  // active contacts that lost the LevitanMD tag in Spruce for admin-confirmed local archival.
  const handleLevitanSync = async () => {
    setIsSyncing(true);
    try {
      const res = await contactSyncApi.levitanmdPreview();
      setSyncPreview(res.data?.data || null);
      setSyncDialogOpen(true);
    } catch (err) {
      console.error('LevitanMD sync failed:', err);
      setError('Spruce sync failed');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleArchiveDetagged = async () => {
    const ids = (syncPreview?.candidates || []).map((c: any) => c.id);
    if (!ids.length) { setSyncDialogOpen(false); return; }
    setIsArchivingDetag(true);
    try {
      const res = await contactSyncApi.levitanmdArchive(ids);
      const n = res.data?.data?.archived ?? 0;
      setSuccessMessage(`Sync complete — archived ${n} de-tagged contact${n === 1 ? '' : 's'}.`);
      setTimeout(() => setSuccessMessage(null), 5000);
      setSyncDialogOpen(false);
      setSyncPreview(null);
    } catch (err) {
      console.error('Archive de-tagged failed:', err);
      setError('Failed to archive de-tagged contacts');
    } finally {
      setIsArchivingDetag(false);
    }
  };

  const handlePreviewTemplates = async () => {
    try {
      const response = await settingsApi.getSmsTemplates();
      setSmsTemplates(response.data?.templates || {});
      setIsTemplateModalOpen(true);
    } catch (err) {
      console.error('Failed to fetch templates:', err);
      setError('Failed to load SMS templates');
    }
  };

  // Branding handlers
  const handleSaveBranding = async () => {
    setIsSavingBranding(true);
    try {
      await brandingApi.updateBranding(brandingForm);
      updateBranding(brandingForm);
      setSuccessMessage('Branding settings saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to save branding:', err);
      setError('Failed to save branding settings');
    } finally {
      setIsSavingBranding(false);
    }
  };

  const handleLogoFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setLogoFile(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadLogo = async () => {
    if (!logoFile) return;
    
    setIsUploadingLogo(true);
    try {
      const response = await brandingApi.uploadLogo(logoFile);
      const newLogoUrl = response.data?.logoUrl;
      if (newLogoUrl) {
        updateBranding({ logoUrl: newLogoUrl });
        setLogoPreview(newLogoUrl);
        setLogoFile(null);
      }
      setSuccessMessage('Logo uploaded successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to upload logo:', err);
      setError('Failed to upload logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    askConfirm({
      title: 'Remove the practice logo?',
      body: 'The app goes back to the default mark until a new logo is uploaded.',
      confirmLabel: 'Remove logo',
      run: async () => {
    try {
      await brandingApi.removeLogo();
      updateBranding({ logoUrl: null });
      setLogoPreview(null);
      setLogoFile(null);
      setSuccessMessage('Logo removed successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to remove logo:', err);
      setError('Failed to remove logo');
    }
      },
    });
  };

  // AI Training handlers (document upload only — voice tone lives in Voice Skill Library)
  const loadAiSkills = async () => {
    try {
      const response = await aiSkillsApi.list();
      const rows = response.data?.data || response.data || [];
      setAiSkills(Array.isArray(rows) ? rows : []);
    } catch (err) {
      console.error('Failed to load AI skills:', err);
    }
  };

  const loadProxyIntake = async () => {
    try {
      const res = await proxyIntakeApi.get();
      const cfg = res.data?.data || {};
      setProxyIntakeEnabled(!!cfg.enabled);
      setProxyPhonesText(Array.isArray(cfg.phones) ? cfg.phones.join('\n') : '');
      setProxyContactIdsText(Array.isArray(cfg.spruceContactIds) ? cfg.spruceContactIds.join('\n') : '');
    } catch (err) {
      console.error('Failed to load proxy intake config:', err);
    }
  };

  const handleSaveProxyIntake = async () => {
    setProxyIntakeSaving(true);
    try {
      const phones = proxyPhonesText.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
      const spruceContactIds = proxyContactIdsText.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
      await proxyIntakeApi.update({ enabled: proxyIntakeEnabled, phones, spruceContactIds });
      setSuccessMessage('Proxy intake settings saved.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (e: any) {
      setError(e?.response?.status === 403
        ? 'Only an admin can change proxy intake settings.'
        : 'Failed to save proxy intake settings.');
    } finally {
      setProxyIntakeSaving(false);
    }
  };

  const handleProxyPreview = async () => {
    try {
      const res = await proxyIntakeApi.preview({ text: proxyPreviewText });
      setProxyPreviewResult(res.data?.data || null);
    } catch (e) {
      setProxyPreviewResult({ error: 'Preview failed' });
    }
  };

  const loadFaxSignatures = async () => {
    try {
      const res = await faxSignaturesApi.get();
      const cfg = res.data?.data || {};
      setFaxSignEnabled(!!cfg.enabled);
      setFaxSignPhonesText(Array.isArray(cfg.phones) ? cfg.phones.join('\n') : '');
      setFaxAutoSendPhonesText(Array.isArray(cfg.autoSendPhones) ? cfg.autoSendPhones.join('\n') : '');
      setFaxSignCues(cfg.alsoMatchTextCues !== false);
      setFaxAutoSendEnabled(!!cfg.autoSendEnabled);
      setFaxSuggestedPhones(Array.isArray(cfg.suggestedPhones) ? cfg.suggestedPhones : []);
      setFaxPracticeSigUrl(cfg.practiceSignatureUrl || null);
    } catch (err) {
      console.error('Failed to load fax signature config:', err);
    }
    try {
      const prefs = await faxFilingsApi.getPrefs();
      setFaxFiledRotationDays(prefs.data?.data?.rotationDays || 7);
    } catch (err) {
      console.error('Failed to load faxes-filed prefs:', err);
    }
  };

  const handleSaveFaxFiledPrefs = async () => {
    setFaxFiledSaving(true);
    try {
      const res = await faxFilingsApi.updatePrefs(faxFiledRotationDays);
      setFaxFiledRotationDays(res.data?.data?.rotationDays || faxFiledRotationDays);
      setSuccessMessage('Faxes filed rotation saved for this login.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to save Faxes filed rotation.');
    } finally {
      setFaxFiledSaving(false);
    }
  };

  const handleSaveFaxSignatures = async () => {
    setFaxSignSaving(true);
    try {
      const phones = faxSignPhonesText.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
      const autoSendPhones = faxAutoSendPhonesText.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean);
      await faxSignaturesApi.update({
        enabled: faxSignEnabled,
        phones,
        autoSendPhones,
        alsoMatchTextCues: faxSignCues,
        autoSendEnabled: faxAutoSendEnabled,
      });
      setSuccessMessage('Faxes-to-sign settings saved.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (e: any) {
      setError(e?.response?.status === 403
        ? 'Only an admin can change faxes-to-sign settings.'
        : 'Failed to save faxes-to-sign settings.');
    } finally {
      setFaxSignSaving(false);
    }
  };

  const handleSaveAiSkill = async (key: string, patch: { aggressiveness?: string; enabled?: boolean }) => {
    setAiSkillsSaving(key);
    try {
      const res = await aiSkillsApi.update(key, patch);
      const updated = res.data?.data;
      if (updated) {
        setAiSkills((prev) => prev.map((s) => (s.key === key ? { ...s, ...updated } : s)));
      } else {
        await loadAiSkills();
      }
      setSuccessMessage('Compression skill saved — takes effect on the next AI dossier / inbound.');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (e: any) {
      setError(e?.response?.status === 403
        ? 'Only an admin can change compression skills.'
        : 'Failed to save compression skill.');
    } finally {
      setAiSkillsSaving(null);
    }
  };

  // Document upload → training. Text files (.txt/.md) are read client-side and
  // sent through the existing text-training pipeline. PDF/Word need a backend
  // extraction step that isn't built yet — flag it rather than fail silently.
  const trainingFileInputRef = useRef<HTMLInputElement | null>(null);
  const handleTrainingFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const name = file.name.toLowerCase();
    const isText = name.endsWith('.txt') || name.endsWith('.md') || file.type.startsWith('text/');
    if (!isText) {
      setTrainingMessage({ type: 'error', text: 'PDF and Word parsing isn’t enabled yet — paste the text into the box above, or ask the team to turn on document parsing.' });
      return;
    }
    setIsUploadingTraining(true);
    setTrainingMessage(null);
    try {
      const text = await file.text();
      if (!text.trim() || text.trim().length < 10) {
        setTrainingMessage({ type: 'error', text: 'That file has no usable text (minimum 10 characters).' });
        return;
      }
      await aiTrainingApi.uploadText(text);
      setTrainingMessage({ type: 'success', text: `Uploaded “${file.name}” as clinic knowledge.` });
    } catch (err: any) {
      setTrainingMessage({ type: 'error', text: err?.response?.data?.error || 'Failed to upload that file.' });
    } finally {
      setIsUploadingTraining(false);
      if (trainingFileInputRef.current) trainingFileInputRef.current.value = '';
    }
  };

  // Specialist handlers
  const handleOpenSpecialistModal = (specialist?: Specialist) => {
    if (specialist) {
      setEditingSpecialist(specialist);
      setSpecialistForm({
        name: specialist.name,
        phone: specialist.phone || '',
        email: specialist.email || '',
        isActive: specialist.isActive,
        workingHours: specialist.workingHours || DEFAULT_WORKING_HOURS,
      });
    } else {
      setEditingSpecialist(null);
      setSpecialistForm({ 
        name: '', 
        phone: '', 
        email: '', 
        isActive: true,
        workingHours: DEFAULT_WORKING_HOURS,
      });
    }
    setIsSpecialistModalOpen(true);
  };

  const handleSaveSpecialist = async () => {
    try {
      // Build workingDays from workingHours (days with at least one time block)
      const workingDays = Object.entries(specialistForm.workingHours)
        .filter(([_, blocks]) => blocks && blocks.length > 0)
        .map(([day]) => day);

      const payload = {
        ...specialistForm,
        workingDays,
      };

      if (editingSpecialist) {
        await specialistsApi.updateSpecialist(editingSpecialist.id, payload);
      } else {
        await specialistsApi.createSpecialist(payload);
      }
      fetchData();
      setIsSpecialistModalOpen(false);
      setSuccessMessage('Provider saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to save specialist:', err);
      setError('Failed to save provider');
    }
  };

  const handleDeleteSpecialist = async (id: string) => {
    const provider = specialists.find((s) => s.id === id);
    askConfirm({
      title: 'Delete this provider?',
      body: `${provider?.name || 'This provider'} is removed from the practice, along with their availability and any service pairings that name them. Appointments already booked are not deleted.`,
      confirmLabel: 'Delete provider',
      run: async () => {
        try {
          await specialistsApi.deleteSpecialist(id);
          fetchData();
        } catch (err) {
          console.error('Failed to delete specialist:', err);
          setError('Failed to delete provider');
        }
      },
    });
  };

  // Working hours handlers
  const handleToggleDay = (day: keyof WorkingHours) => {
    setSpecialistForm(prev => {
      const currentBlocks = prev.workingHours[day];
      if (currentBlocks && currentBlocks.length > 0) {
        // Remove blocks (not working)
        return {
          ...prev,
          workingHours: { ...prev.workingHours, [day]: [] },
        };
      } else {
        // Add default blocks
        return {
          ...prev,
          workingHours: { 
            ...prev.workingHours, 
            [day]: [{ start: '09:00', end: '12:00' }, { start: '13:00', end: '17:00' }] 
          },
        };
      }
    });
  };

  const handleUpdateTimeBlock = (
    day: keyof WorkingHours, 
    blockIndex: number, 
    field: 'start' | 'end', 
    value: string
  ) => {
    setSpecialistForm(prev => {
      const dayBlocks = [...(prev.workingHours[day] || [])];
      if (dayBlocks[blockIndex]) {
        dayBlocks[blockIndex] = { ...dayBlocks[blockIndex], [field]: value };
      }
      return {
        ...prev,
        workingHours: { ...prev.workingHours, [day]: dayBlocks },
      };
    });
  };

  const handleAddTimeBlock = (day: keyof WorkingHours) => {
    setSpecialistForm(prev => {
      const dayBlocks = [...(prev.workingHours[day] || [])];
      dayBlocks.push({ start: '13:00', end: '17:00' });
      return {
        ...prev,
        workingHours: { ...prev.workingHours, [day]: dayBlocks },
      };
    });
  };

  const handleRemoveTimeBlock = (day: keyof WorkingHours, blockIndex: number) => {
    setSpecialistForm(prev => {
      const dayBlocks = prev.workingHours[day].filter((_, i) => i !== blockIndex);
      return {
        ...prev,
        workingHours: { ...prev.workingHours, [day]: dayBlocks },
      };
    });
  };

  // Service handlers
  const handleOpenServiceModal = (service?: Service) => {
    if (service) {
      setEditingService(service);
      setServiceForm({
        name: service.name,
        duration: service.duration,
        isActive: service.isActive,
      });
    } else {
      setEditingService(null);
      setServiceForm({ name: '', duration: 30, isActive: true });
    }
    setIsServiceModalOpen(true);
  };

  const handleSaveService = async () => {
    try {
      if (editingService) {
        await servicesApi.updateService(editingService.id, serviceForm);
      } else {
        await servicesApi.createService(serviceForm);
      }
      fetchData();
      setIsServiceModalOpen(false);
    } catch (err) {
      console.error('Failed to save service:', err);
      setError('Failed to save service');
    }
  };

  const handleDeleteService = async (id: string) => {
    const service = services.find((s) => s.id === id);
    askConfirm({
      title: 'Delete this service?',
      body: `${service?.name || 'This service'} can no longer be booked, and the provider pairings that use it go with it.`,
      confirmLabel: 'Delete service',
      run: async () => {
        try {
          await servicesApi.deleteService(id);
          fetchData();
        } catch (err) {
          console.error('Failed to delete service:', err);
          setError('Failed to delete service');
        }
      },
    });
  };

  // Mapping handlers
  const handleOpenMappingModal = () => {
    setMappingForm({ serviceId: '', specialistId: '' });
    setIsMappingModalOpen(true);
  };

  const handleSaveMapping = async () => {
    try {
      await serviceMappingApi.create(mappingForm.serviceId, mappingForm.specialistId);
      fetchData();
      setIsMappingModalOpen(false);
    } catch (err) {
      console.error('Failed to create mapping:', err);
      setError('Failed to create mapping');
    }
  };

  // Which mapping's duration is being edited, and the value in the box.
  const [editingDuration, setEditingDuration] = useState<{ id: string; value: string } | null>(null);

  const handleDeleteMapping = async (id: string) => {
    const mapping = mappings.find((m) => m.id === id);
    const pairing = mapping
      ? `${mapping.specialist?.name || 'This provider'} — ${mapping.service?.name || 'this service'}`
      : 'This pairing';
    askConfirm({
      title: 'Remove this pairing?',
      body: `${pairing} stops being offered, and any duration set for it is lost.`,
      confirmLabel: 'Remove pairing',
      run: async () => {
        try {
          await serviceMappingApi.delete(id);
          fetchData();
        } catch (err) {
          console.error('Failed to delete mapping:', err);
          setError('Failed to delete mapping');
        }
      },
    });
  };

  // Change how long this provider books this service for. An empty field
  // clears the override, so the pairing goes back to the service's own
  // duration rather than being pinned to whatever it happened to be.
  const handleSaveMappingDuration = async (id: string, raw: string) => {
    const trimmed = raw.trim();
    const duration = trimmed === '' ? null : Number(trimmed);
    if (duration !== null && (!Number.isFinite(duration) || duration <= 0)) {
      setError('Duration must be a number of minutes, or empty to use the service default');
      return;
    }
    try {
      await serviceMappingApi.updateDuration(id, duration);
      setEditingDuration(null);
      fetchData();
    } catch (err: any) {
      console.error('Failed to update mapping duration:', err);
      setError(err?.response?.data?.error || 'Failed to update the duration');
    }
  };

  // Lab directory handlers
  const handleOpenLabModal = (lab?: LabDirectoryEntry) => {
    if (lab) {
      setEditingLab(lab);
      setLabForm({
        name: lab.name || '',
        category: lab.category || '',
        aliases: (lab.aliases || []).join(', '),
        phoneNumbers: (lab.phone_numbers || []).join(', '),
        notes: lab.notes || '',
        status: lab.status || 'active',
      });
    } else {
      setEditingLab(null);
      setLabForm({
        name: '',
        category: '',
        aliases: '',
        phoneNumbers: '',
        notes: '',
        status: 'active',
      });
    }
    setIsLabModalOpen(true);
  };

  const handleSaveLab = async () => {
    if (!labForm.name.trim()) {
      setError('Lab name is required');
      return;
    }
    setIsSavingLab(true);
    try {
      const payload = {
        name: labForm.name.trim(),
        category: labForm.category.trim() || null,
        aliases: parseCommaList(labForm.aliases),
        phoneNumbers: parseCommaList(labForm.phoneNumbers),
        notes: labForm.notes.trim() || null,
        status: labForm.status,
      };
      if (editingLab) {
        await labDirectoryApi.update(editingLab.id, payload);
      } else {
        await labDirectoryApi.create(payload);
      }
      fetchData();
      setIsLabModalOpen(false);
      setSuccessMessage('Lab saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to save lab:', err);
      setError('Failed to save lab');
    } finally {
      setIsSavingLab(false);
    }
  };

  const handleDeactivateLab = async (lab: LabDirectoryEntry) => {
    askConfirm({
      title: 'Deactivate this lab?',
      body: `${lab.name} stops being offered for new orders. Existing results already filed against it are untouched.`,
      confirmLabel: 'Deactivate lab',
      run: async () => {
        try {
          await labDirectoryApi.deactivate(lab.id);
          fetchData();
        } catch (err) {
          console.error('Failed to deactivate lab:', err);
          setError('Failed to deactivate lab');
        }
      },
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header — density pass: smaller responsive title, mb 3→2 */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.35rem', md: '1.6rem' } }}>
            Admin Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage AI behavior, scheduling, providers, and practice configuration
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Section navigation — a dropdown selector on mobile (all 7 sections
          visible in one tap, no horizontal scroll) and the tab bar on desktop. */}
      {isMobile ? (
        <Paper sx={{ mb: 2, p: 1 }}>
          <TextField
            select
            fullWidth
            size="small"
            label="Section"
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value as any)}
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
          >
            <option value="aitraining">AI</option>
            <option value="intake">Intake / Proxy</option>
            <option value="faxtosign">Faxes</option>
            <option value="settings">Settings</option>
            <option value="providers">Providers</option>
            <option value="services">Services</option>
            <option value="mappings">Provider-Service Mapping</option>
            <option value="labs">Labs</option>
            <option value="branding">Branding</option>
          </TextField>
        </Paper>
      ) : (
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 40,
            '& .MuiTab-root': { minHeight: 40, py: 0.5 },
          }}
        >
          <Tab
            label="AI"
            value="aitraining"
            icon={<PsychologyIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            label="Intake / Proxy"
            value="intake"
            icon={<IntakeIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            label="Faxes"
            value="faxtosign"
            icon={<FaxTabIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            label="Settings"
            value="settings"
            icon={<SettingsIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            label="Providers"
            value="providers"
            icon={<PersonIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            label="Services"
            value="services"
            icon={<ServiceIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            label="Provider-Service Mapping"
            value="mappings"
            icon={<LinkIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            label="Labs"
            value="labs"
            icon={<ScienceIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
          <Tab
            label="Branding"
            value="branding"
            icon={<PaletteIcon />}
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
        </Tabs>
      </Paper>
      )}

      {/* Providers Tab */}
      {activeTab === 'providers' && (
        <Paper sx={{ p: { xs: 1.25, sm: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">Providers / Specialists</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleOpenSpecialistModal()}
            >
              Add Provider
            </Button>
          </Box>
          
          {/* Provider Cards with Working Hours */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {specialists.map((specialist) => (
              <Paper key={specialist.id} variant="outlined" sx={{ p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Typography variant="h6">{specialist.name}</Typography>
                      <Chip
                        label={specialist.isActive ? 'Active' : 'Inactive'}
                        color={specialist.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {specialist.email || 'No email'} • {specialist.phone || 'No phone'}
                    </Typography>
                    
                    {/* Working Hours Display — aligned 7-day strip */}
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(7, 1fr)',
                        gap: 0.5,
                        maxWidth: 520,
                      }}
                    >
                      {DAYS_OF_WEEK.map((day) => {
                        const dayBlocks = specialist.workingHours?.[day.key as keyof WorkingHours] || [];
                        const isWorking = dayBlocks.length > 0;
                        const { span, hasBreak } = formatDaySpan(dayBlocks);
                        const cell = (
                          <Box
                            sx={{
                              borderRadius: 1.5,
                              px: 0.5,
                              py: 0.5,
                              textAlign: 'center',
                              border: '1px solid',
                              borderColor: (t) => isWorking ? alpha(t.palette.primary.main, 0.22) : 'divider',
                              bgcolor: (t) => isWorking ? alpha(t.palette.primary.main, 0.07) : 'transparent',
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                fontWeight: 700,
                                lineHeight: 1.3,
                                fontSize: '0.66rem',
                                letterSpacing: '.02em',
                                color: isWorking ? 'primary.main' : 'text.disabled',
                              }}
                            >
                              {day.short}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                lineHeight: 1.2,
                                fontSize: '0.62rem',
                                fontVariantNumeric: 'tabular-nums',
                                color: isWorking ? 'text.primary' : 'text.disabled',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {isWorking ? span : '–'}
                              {hasBreak && (
                                <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>·</Box>
                              )}
                            </Typography>
                          </Box>
                        );
                        return isWorking && hasBreak ? (
                          <Tooltip key={day.key} title={formatTimeBlocks(dayBlocks)} arrow>
                            {cell}
                          </Tooltip>
                        ) : (
                          <React.Fragment key={day.key}>{cell}</React.Fragment>
                        );
                      })}
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => handleOpenSpecialistModal(specialist)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteSpecialist(specialist.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        </Paper>
      )}

      {/* Services Tab */}
      {activeTab === 'services' && (
        <Paper sx={{ p: { xs: 1.25, sm: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">Appointment Types / Services</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleOpenServiceModal()}
            >
              Add Service
            </Button>
          </Box>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {services.map((service) => (
                <MobileRecordCard
                  key={service.id}
                  title={service.name}
                  status={{ label: service.isActive ? 'Active' : 'Inactive', color: service.isActive ? 'success' : 'default' }}
                  rows={[{ label: 'Duration', value: `${service.duration} mins` }]}
                  actions={
                    <>
                      <IconButton size="small" onClick={() => handleOpenServiceModal(service)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteService(service.id)}><DeleteIcon fontSize="small" /></IconButton>
                    </>
                  }
                />
              ))}
            </Box>
          ) : (
          <TableContainer>
            <Table sx={{ minWidth: 500 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Duration (mins)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.map((service) => (
                  <TableRow key={service.id}>
                    <TableCell>{service.name}</TableCell>
                    <TableCell>{service.duration}</TableCell>
                    <TableCell>
                      <Chip
                        label={service.isActive ? 'Active' : 'Inactive'}
                        color={service.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleOpenServiceModal(service)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteService(service.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Paper>
      )}

      {/* Mappings Tab */}
      {activeTab === 'mappings' && (
        <Paper sx={{ p: { xs: 1.25, sm: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">Provider-Service Mappings</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleOpenMappingModal}
            >
              Add Mapping
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Define which services each provider can offer
          </Typography>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {mappings.map((mapping) => (
                <MobileRecordCard
                  key={mapping.id}
                  title={mapping.specialist?.name}
                  rows={[
                    { label: 'Service', value: mapping.service?.name },
                    { label: 'Duration', value: describeMappingDuration(mapping) },
                  ]}
                  actions={
                    <IconButton size="small" color="error" onClick={() => handleDeleteMapping(mapping.id)}><DeleteIcon fontSize="small" /></IconButton>
                  }
                />
              ))}
            </Box>
          ) : (
          <TableContainer>
            <Table sx={{ minWidth: 500 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Provider</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mappings.map((mapping) => (
                  <TableRow key={mapping.id}>
                    <TableCell>{mapping.specialist?.name}</TableCell>
                    <TableCell>{mapping.service?.name}</TableCell>
                    <TableCell>
                      {editingDuration?.id === mapping.id ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TextField
                            size="small"
                            autoFocus
                            type="number"
                            value={editingDuration.value}
                            placeholder={String(mapping.service?.duration ?? '')}
                            onChange={(e) => setEditingDuration({ id: mapping.id, value: e.target.value })}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveMappingDuration(mapping.id, editingDuration.value);
                              if (e.key === 'Escape') setEditingDuration(null);
                            }}
                            sx={{ width: 96 }}
                            inputProps={{ min: 1, 'aria-label': `Duration in minutes for ${mapping.specialist?.name}` }}
                          />
                          <Typography variant="caption" color="text.secondary">mins</Typography>
                          <IconButton
                            size="small"
                            color="primary"
                            aria-label="Save duration"
                            onClick={() => handleSaveMappingDuration(mapping.id, editingDuration.value)}
                          >
                            <CheckIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" aria-label="Cancel" onClick={() => setEditingDuration(null)}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span>{describeMappingDuration(mapping)}</span>
                          <Tooltip title="Change how long this provider books this service for. Leave it empty to use the service default.">
                            <IconButton
                              size="small"
                              aria-label={`Edit duration for ${mapping.specialist?.name} — ${mapping.service?.name}`}
                              onClick={() => setEditingDuration({
                                id: mapping.id,
                                value: mapping.duration ? String(mapping.duration) : '',
                              })}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => handleDeleteMapping(mapping.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Paper>
      )}

      {/* Labs Tab */}
      {activeTab === 'labs' && (
        <Paper sx={{ p: { xs: 1.25, sm: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">Lab Directory</Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => handleOpenLabModal()}
            >
              Add Lab
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Maintain lab names, aliases, and phone/fax numbers for auto-detection.
          </Typography>
          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {labs.map((lab) => (
                <MobileRecordCard
                  key={lab.id}
                  title={lab.name}
                  status={{ label: lab.status === 'inactive' ? 'Inactive' : 'Active', color: lab.status === 'inactive' ? 'default' : 'success' }}
                  rows={[
                    { label: 'Category', value: lab.category || '—' },
                    { label: 'Phones', value: (lab.phone_numbers || []).join(', ') || '—' },
                  ]}
                  actions={
                    <>
                      <IconButton size="small" onClick={() => handleOpenLabModal(lab)}><EditIcon fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeactivateLab(lab)}><DeleteIcon fontSize="small" /></IconButton>
                    </>
                  }
                />
              ))}
            </Box>
          ) : (
          <TableContainer>
            <Table sx={{ minWidth: 600 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Phones</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {labs.map((lab) => (
                  <TableRow key={lab.id}>
                    <TableCell>{lab.name}</TableCell>
                    <TableCell>{lab.category || '—'}</TableCell>
                    <TableCell>{(lab.phone_numbers || []).join(', ') || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={lab.status === 'inactive' ? 'Inactive' : 'Active'}
                        color={lab.status === 'inactive' ? 'default' : 'success'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleOpenLabModal(lab)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeactivateLab(lab)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )}
        </Paper>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <>
        <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="stretch">
          {/* SMS Notifications */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SettingSection
              icon={<SmsIcon color="primary" />}
              title="SMS Notifications"
              help="These switches decide which texts the practice is allowed to send a patient. Sending is gated again on the server: while outbound messaging is off, nothing reaches a patient no matter what is switched on here."
            >
              <SettingRow
                label="Send confirmation SMS"
                help="Sent the moment a patient books held times through a booking link. Off, the task says so and the office texts them by hand."
                control={
                  <Switch
                    checked={settings.sms_confirmation_enabled}
                    onChange={(e) => setSettings(prev => ({ ...prev, sms_confirmation_enabled: e.target.checked }))}
                    slotProps={{ input: { 'aria-label': 'Send confirmation SMS' } }}
                  />
                }
              />
              <SettingRow
                label="Send reminder SMS"
                help="Goes out ahead of the appointment."
                control={
                  <Switch
                    checked={settings.sms_reminder_enabled}
                    onChange={(e) => setSettings(prev => ({ ...prev, sms_reminder_enabled: e.target.checked }))}
                    slotProps={{ input: { 'aria-label': 'Send reminder SMS' } }}
                  />
                }
              />
              <SettingRow
                label="Reminder time"
                help="How far ahead of the appointment the reminder goes out."
                stack
                control={
                  <TextField
                    select
                    value={settings.sms_reminder_hours}
                    onChange={(e) => setSettings(prev => ({ ...prev, sms_reminder_hours: parseInt(e.target.value) }))}
                    size="small"
                    sx={{ width: { xs: '100%', sm: 164 } }}
                    SelectProps={{ native: true }}
                    disabled={!settings.sms_reminder_enabled}
                    inputProps={{ 'aria-label': 'Reminder time' }}
                  >
                    <option value={1}>1 hour before</option>
                    <option value={2}>2 hours before</option>
                    <option value={4}>4 hours before</option>
                    <option value={24}>24 hours before</option>
                    <option value={48}>48 hours before</option>
                    <option value={72}>72 hours before</option>
                  </TextField>
                }
              />
              <SettingRow
                label="Send cancellation SMS"
                help="Tells the patient when their appointment is cancelled."
                divider={false}
                control={
                  <Switch
                    checked={settings.sms_cancellation_enabled}
                    onChange={(e) => setSettings(prev => ({ ...prev, sms_cancellation_enabled: e.target.checked }))}
                    slotProps={{ input: { 'aria-label': 'Send cancellation SMS' } }}
                  />
                }
              />
              <Box sx={{ flexGrow: 1 }} />
              <Button variant="outlined" size="small" fullWidth onClick={handlePreviewTemplates} sx={{ mt: 2 }}>
                Preview SMS templates
              </Button>
            </SettingSection>
          </Grid>

          {/* Scheduling Options — the calendar's own rules. Booking links and
              wellness outreach each have a card of their own below: in one
              card they made a column three screens tall, with the SMS card
              stretched to match beside it. */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SettingSection
              icon={<ScheduleIcon color="primary" />}
              title="Scheduling Options"
              help="The calendar's own rules. Appointment lengths are not set here — they are set per service under Services, and per provider under Provider-Service Mapping."
            >
              <SettingRow
                label="Clinic time zone"
                help="The calendar shows every time in this zone."
                tip="Changing the zone does not move appointments — a 10:00 booking stays at 10:00. It applies once you save."
                stack
                control={
                  <TextField
                    select
                    value={settings.practice_timezone || 'America/Chicago'}
                    onChange={(e) => setSettings(prev => ({ ...prev, practice_timezone: e.target.value }))}
                    size="small"
                    sx={{ width: { xs: '100%', sm: 220 } }}
                    SelectProps={{ native: true }}
                    inputProps={{ 'aria-label': 'Clinic time zone' }}
                  >
                    <optgroup label="Common">
                      {listPracticeTimeZones().filter((z) => z.group === 'common').map((z) => (
                        <option key={z.value} value={z.value}>{z.label}</option>
                      ))}
                    </optgroup>
                    <optgroup label="All time zones">
                      {listPracticeTimeZones().filter((z) => z.group === 'all').map((z) => (
                        <option key={z.value} value={z.value}>{z.label}</option>
                      ))}
                    </optgroup>
                  </TextField>
                }
              />
              <SettingRow
                label="Buffer between appointments"
                help="Minutes kept free before and after each appointment."
                tip="The calendar will not offer a time that falls inside the buffer. With 30, a visit ending at 10:00 keeps 10:00–10:30 free. Times held by a booking link get the same buffer. Use 0 to allow back-to-back bookings."
                control={
                  <TextField
                    type="number"
                    value={settings.scheduling_buffer_minutes}
                    onChange={(e) => setSettings(prev => ({ ...prev, scheduling_buffer_minutes: parseInt(e.target.value) || 0 }))}
                    size="small"
                    sx={{ width: 112 }}
                    InputProps={{ endAdornment: <InputAdornment position="end">min</InputAdornment> }}
                    inputProps={{ min: 0, step: 5, 'aria-label': 'Buffer between appointments in minutes' }}
                  />
                }
              />
              <SettingRow
                label="Allow same-day bookings"
                help="When off, today is not offered as a bookable day."
                tip="Applies wherever the app offers open times — to staff choosing a time and on patient booking links."
                divider={false}
                control={
                  <Switch
                    checked={settings.scheduling_same_day_allowed}
                    onChange={(e) => setSettings(prev => ({ ...prev, scheduling_same_day_allowed: e.target.checked }))}
                    slotProps={{ input: { 'aria-label': 'Allow same-day bookings' } }}
                  />
                }
              />
            </SettingSection>
          </Grid>

          {/* Booking Links */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SettingSection
              icon={<BookingLinkIcon color="primary" />}
              title="Booking Links"
              help="A booking link is a one-time web page staff text to a patient. The office picks the times, the link holds them for that patient, and the patient taps to book."
              chip={patientSelfBooking ? undefined : <Chip label="Off" size="small" variant="outlined" />}
            >
              <SettingRow
                label="Patient booking links"
                help="Let staff send patients a link to book online."
                tip="With this off no booking link can be created — including for wellness outreach — and links already sent stop opening."
                control={
                  <Switch
                    checked={patientSelfBooking}
                    onChange={(e) => setPatientSelfBooking(e.target.checked)}
                    slotProps={{ input: { 'aria-label': 'Patient booking links' } }}
                  />
                }
              />
              <SettingRow
                label="Hold times for"
                help="How long a link, and the times it holds, stay open."
                tip="When the time is up the link stops working and its times go back on the calendar for everyone else. Links already sent keep the deadline they were given."
                control={
                  <TextField
                    select
                    SelectProps={{ native: true }}
                    value={holdDays(settings.scheduling_link_ttl_days)}
                    onChange={(e) => setSettings(prev => ({ ...prev, scheduling_link_ttl_days: Number(e.target.value) }))}
                    size="small"
                    sx={{ width: 112 }}
                    inputProps={{ 'aria-label': 'How long a booking link holds its times' }}
                  >
                    <option value={1}>1 day</option>
                    <option value={2}>2 days</option>
                    <option value={3}>3 days</option>
                  </TextField>
                }
              />
              <SettingRow
                label="Practice phone"
                help="Shown to patients on the booking page and in the confirmation text."
                tip="Patients are told to reply to the text, or call this number. Leave it empty and they are only told to reply — the page never points to a number it does not show."
                stack
                control={
                  <TextField
                    value={settings.practice_phone || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, practice_phone: e.target.value }))}
                    size="small"
                    placeholder="(847) 555-0100"
                    sx={{ width: { xs: '100%', sm: 180 } }}
                    inputProps={{ 'aria-label': 'Practice phone', inputMode: 'tel', autoComplete: 'off' }}
                  />
                }
              />
              {/* Blank is a real choice, not an unset field: it means the
                  standard wording, which the app writes itself and which copes
                  with one time, several, and two appointments on one link. */}
              <MessageSetting
                label="Booking message"
                help="The text a patient gets with their link. Leave it empty to use the standard wording."
                tip="Each button below adds a tag the app fills in for the patient. Your wording is used whether one time is held or several, so write it to suit both. A link carrying two appointments always uses the standard wording."
                value={settings.scheduling_offer_message}
                onChange={(next) => setSettings(prev => ({ ...prev, scheduling_offer_message: next }))}
                tags={OFFER_TAG_BUTTONS}
                standard={STANDARD_OFFER_TEMPLATE}
                preview={(template) => heldOfferText({
                  patientName: 'Jane Doe',
                  slots: ['10:00', '13:00'].map((time) => ({
                    date: sampleDay(), time, specialist: 'Dr. Victor Levitan', serviceType: 'Consultation',
                  })),
                  url: sampleLink(),
                  expiresAt: new Date(Date.now() + holdDays(settings.scheduling_link_ttl_days) * 86400000),
                  timeZone: settings.practice_timezone || 'America/Chicago',
                  template,
                })}
              />
              {/* What a patient is sent after booking held times through a
                  link — if "Send confirmation SMS" is on. Until this existed
                  they saw "You are booked" on the page and heard nothing.
                  Blank means the standard wording, which also copes with two
                  appointments; it carries no link, so it is not warned for
                  having none. */}
              <MessageSetting
                label="Confirmation text"
                help="The text message a patient gets after booking through a link. The booking page shows its own confirmation. Leave it empty to use the standard wording."
                tip="Sent only when “Send confirmation SMS” is on, and only for times the office held on a link. The Practice phone tag is the number above; left empty, it fills in as nothing. A link that booked two appointments always uses the standard wording."
                value={settings.scheduling_confirmation_message || ''}
                onChange={(next) => setSettings(prev => ({ ...prev, scheduling_confirmation_message: next }))}
                tags={CONFIRMATION_TAG_BUTTONS}
                standard={STANDARD_CONFIRMATION_TEMPLATE}
                requireLink={false}
                divider={false}
                preview={(template) => confirmationText({
                  patientName: 'Jane Doe',
                  appointments: [{ date: sampleDay(), time: '10:00', providerName: 'Dr. Victor Levitan', serviceName: 'Consultation' }],
                  phone: settings.practice_phone,
                  template,
                })}
              />
            </SettingSection>
          </Grid>

          {/* Wellness Outreach */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SettingSection
              icon={<WellnessIcon color="primary" />}
              title="Wellness Outreach"
              help="The text and booking link a patient gets when the office reaches out about a wellness exam that is due."
            >
              <SettingRow
                label="Overdue patients per month"
                help="How many overdue patients to contact each month."
                tip="The starting number when the calendar spreads patients 18+ months overdue across the coming months, so a backlog is not all contacted at once. It can be changed for any one run."
                control={
                  <TextField
                    type="number"
                    value={settings.scheduling_wellness_spread_cap}
                    onChange={(e) => setSettings(prev => ({ ...prev, scheduling_wellness_spread_cap: Math.max(1, parseInt(e.target.value) || 1) }))}
                    size="small"
                    sx={{ width: 112 }}
                    inputProps={{ min: 1, step: 1, 'aria-label': 'Overdue patients to contact per month' }}
                  />
                }
              />
              <SettingRow
                label="Link stays open for"
                help="Days before an outreach link stops working."
                tip="Longer than an ordinary booking link, because outreach is not urgent. Links already sent keep the deadline they were given."
                control={
                  <TextField
                    type="number"
                    value={settings.scheduling_wellness_link_ttl_days}
                    onChange={(e) => setSettings(prev => ({ ...prev, scheduling_wellness_link_ttl_days: Math.max(1, parseInt(e.target.value) || 1) }))}
                    size="small"
                    sx={{ width: 112 }}
                    InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                    inputProps={{ min: 1, step: 1, 'aria-label': 'Days a wellness outreach link lasts' }}
                  />
                }
              />
              <MessageSetting
                label="Outreach message"
                help="The text a patient gets when their wellness exam is due. Leave it empty to use the standard wording."
                tip="Each button below adds a tag the app fills in for the patient. Expires becomes a phrase taken from the setting above, such as “in 30 days”."
                value={settings.wellness_outreach_message}
                onChange={(next) => setSettings(prev => ({ ...prev, wellness_outreach_message: next }))}
                tags={OUTREACH_TAG_BUTTONS}
                standard={STANDARD_OUTREACH_TEMPLATE}
                divider={false}
                preview={(template) => renderOutreach(template.trim() ? template : STANDARD_OUTREACH_TEMPLATE, {
                  firstName: 'Jane Doe',
                  provider: 'Dr. Victor Levitan',
                  link: sampleLink(),
                  expires: linkLifetimePhrase(settings.scheduling_wellness_link_ttl_days),
                })}
              />
            </SettingSection>
          </Grid>

          {/* AI Automation */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SettingSection
              icon={<AutomationIcon color={settings.appointment_triggers_enabled ? 'warning' : 'primary'} />}
              title="AI Automation"
              accent={settings.appointment_triggers_enabled ? 'warning' : undefined}
              chip={settings.appointment_triggers_enabled
                ? <Chip label="Active" color="warning" size="small" />
                : undefined}
            >
              <Alert severity={settings.appointment_triggers_enabled ? 'warning' : 'info'} sx={{ mb: 0.5 }}>
                {settings.appointment_triggers_enabled
                  ? 'The AI drafts scheduling replies. Every draft still needs a person to approve it before anything is sent.'
                  : 'AI scheduling drafts are off. Messages require manual review.'}
              </Alert>
              <SettingRow
                label="Enable AI scheduling drafts"
                help="The AI spots a scheduling request and writes a reply for staff to approve. It never sends on its own."
                divider={false}
                control={
                  <Switch
                    checked={settings.appointment_triggers_enabled}
                    onChange={(e) => setSettings(prev => ({ ...prev, appointment_triggers_enabled: e.target.checked }))}
                    color="warning"
                    slotProps={{ input: { 'aria-label': 'Enable AI scheduling drafts' } }}
                  />
                }
              />
              <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                  Messages containing these words trigger a draft
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {['appointment', 'schedule', 'book', 'urgent', 'asap'].map((k) => (
                    <Chip key={k} label={k} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            </SettingSection>
          </Grid>

          {/* Pleasantries Auto-Response */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SettingSection
              icon={<PleasantriesIcon color={settings.pleasantries_auto_response_enabled ? 'error' : 'primary'} />}
              title="Pleasantries Auto-Response"
              accent={settings.pleasantries_auto_response_enabled ? 'error' : undefined}
              chip={settings.pleasantries_auto_response_enabled
                ? <Chip label="Active" color="error" size="small" />
                : undefined}
            >
              <Alert severity={settings.pleasantries_auto_response_enabled ? 'error' : 'info'} sx={{ mb: 0.5 }}>
                {settings.pleasantries_auto_response_enabled
                  ? 'The AI replies to simple messages on its own, with no human review.'
                  : 'Pleasantry auto-responses are off. Every message requires manual review.'}
              </Alert>
              <SettingRow
                label="Enable pleasantries auto-response"
                help="Short closing messages get an automatic reply. This is the one setting here that sends without a person reading it first."
                divider={false}
                control={
                  <Switch
                    checked={settings.pleasantries_auto_response_enabled}
                    onChange={(e) => setSettings(prev => ({ ...prev, pleasantries_auto_response_enabled: e.target.checked }))}
                    color="error"
                    slotProps={{ input: { 'aria-label': 'Enable pleasantries auto-response' } }}
                  />
                }
              />
              <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                  Handled automatically without staff review
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {['thank you', 'thanks', 'ok', 'okay', 'got it', 'goodbye', 'bye'].map((k) => (
                    <Chip key={k} label={k} size="small" variant="outlined" />
                  ))}
                </Box>
              </Box>
            </SettingSection>
          </Grid>

          {/* Spruce Contact Sync */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SettingSection
              icon={<LinkIcon color="primary" />}
              title="Spruce Contact Sync"
              help="Keeps the local contact list in step with the LevitanMD-tagged contacts in Spruce."
              action={
                <Tooltip title="View archived contacts (opens in a new tab)">
                  <IconButton
                    size="small"
                    aria-label="View archived contacts"
                    onClick={() => window.open('/contacts?view=archived', '_blank', 'noopener')}
                  >
                    <ArchiveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              <SettingRow
                label="Automatic sync"
                help={`Contacts sync between Spruce and the local database every ${settings.contact_sync_interval_minutes} minutes.`}
                divider={false}
                control={
                  <Switch
                    checked={settings.contact_sync_enabled}
                    onChange={(e) => setSettings(prev => ({ ...prev, contact_sync_enabled: e.target.checked }))}
                    slotProps={{ input: { 'aria-label': 'Enable automatic contact sync' } }}
                  />
                }
              />
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="outlined"
                size="small"
                fullWidth
                startIcon={isSyncing ? <CircularProgress size={16} /> : <SyncIcon fontSize="small" />}
                onClick={handleLevitanSync}
                disabled={isSyncing}
                sx={{ mt: 2 }}
              >
                {isSyncing ? 'Syncing…' : 'Sync LevitanMD contacts'}
              </Button>
            </SettingSection>
          </Grid>

          {/* Save */}
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column-reverse', sm: 'row' },
                justifyContent: 'flex-end',
                gap: 1.5,
                pt: 2,
                mt: 0.5,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Button
                variant="outlined"
                onClick={handleResetSettings}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Reset to defaults
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveSettings}
                disabled={isSavingSettings}
                startIcon={isSavingSettings ? <CircularProgress size={16} color="inherit" /> : <SaveIcon fontSize="small" />}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                {isSavingSettings ? 'Saving…' : 'Save settings'}
              </Button>
            </Box>
          </Grid>
        </Grid>

          <Dialog
            open={syncDialogOpen}
            onClose={() => !isArchivingDetag && setSyncDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Spruce Sync — LevitanMD Contacts</DialogTitle>
            <DialogContent dividers>
              {syncPreview && (
                <>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Pulled {syncPreview.pull?.tagged ?? 0} LevitanMD-tagged contact(s) from Spruce
                    {' '}({syncPreview.pull?.created ?? 0} new, {syncPreview.pull?.updated ?? 0} updated).
                  </Typography>
                  {!syncPreview.pullHealthy ? (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                      The Spruce pull looks incomplete, so de-tag archiving is disabled for this run
                      (safety guard). Active contacts were still refreshed.
                    </Alert>
                  ) : syncPreview.candidateCount > 0 ? (
                    <>
                      <Alert severity="warning" sx={{ my: 1 }}>
                        {syncPreview.candidateCount} active contact(s) no longer carry the LevitanMD
                        tag in Spruce. Review and confirm to archive them locally.
                      </Alert>
                      <Box sx={{ maxHeight: 240, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1 }}>
                        {syncPreview.candidates.map((c: any) => (
                          <Typography key={c.id} variant="body2" sx={{ py: 0.25 }}>
                            {c.internalId ? `${c.internalId} — ` : ''}{c.fullName || '(no name)'}{c.phone ? ` · ${c.phone}` : ''}
                          </Typography>
                        ))}
                      </Box>
                    </>
                  ) : (
                    <Alert severity="success" sx={{ mt: 1 }}>
                      No de-tagged contacts. Your active set is in sync.
                    </Alert>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSyncDialogOpen(false)} disabled={isArchivingDetag}>
                {syncPreview?.pullHealthy && syncPreview?.candidateCount > 0 ? 'Keep them (skip archiving)' : 'Close'}
              </Button>
              {syncPreview?.pullHealthy && syncPreview?.candidateCount > 0 && (
                <Button
                  color="warning"
                  variant="contained"
                  onClick={handleArchiveDetagged}
                  disabled={isArchivingDetag}
                  startIcon={isArchivingDetag ? <CircularProgress size={16} /> : <ArchiveIcon fontSize="small" />}
                >
                  {isArchivingDetag ? 'Archiving...' : `Archive ${syncPreview.candidateCount} contact${syncPreview.candidateCount === 1 ? '' : 's'}`}
                </Button>
              )}
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* Branding Tab */}

      {activeTab === 'intake' && (
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          <Grid size={{ xs: 12 }}>
            <Card elevation={2}>
              <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, sm: 1.5 } }}>
                  <IntakeIcon color="primary" />
                  <Typography variant="h6">Proxy SMS Intake</Typography>
                  <InfoTip text="Staff text Spruce from a personal phone with the patient name on line 1 and the task on the following lines. Only numbers / Spruce contact IDs listed here are rewritten. Ambiguous or unknown names create a triage card marked Needs patient — never auto-link the wrong chart." />
                </Box>
                <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />

                <FormControlLabel
                  control={
                    <Switch
                      checked={proxyIntakeEnabled}
                      onChange={(_, checked) => setProxyIntakeEnabled(checked)}
                    />
                  }
                  label="Enable proxy intake"
                  sx={{ mb: 1.5 }}
                />

                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Staff proxy phones</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                  One per line (any common format). Messages from these numbers are treated as proxy intake.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  value={proxyPhonesText}
                  onChange={(e) => setProxyPhonesText(e.target.value)}
                  placeholder={"(305) 555-0100\n+13055550101"}
                  sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Spruce contact IDs (optional)</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                  If the staff phone is already a Spruce contact, you can list their entity id (e.g. entity_…) instead of or in addition to the phone.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  value={proxyContactIdsText}
                  onChange={(e) => setProxyContactIdsText(e.target.value)}
                  placeholder="entity_…"
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button variant="contained" onClick={handleSaveProxyIntake} disabled={proxyIntakeSaving}>
                    {proxyIntakeSaving ? 'Saving…' : 'Save Intake / Proxy'}
                  </Button>
                </Box>

                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Format</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Line 1 = patient full name · following lines = task details (iOS Shortcut / SMS).
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Preview sample"
                  value={proxyPreviewText}
                  onChange={(e) => setProxyPreviewText(e.target.value)}
                  sx={{ mb: 1 }}
                />
                <Button size="small" variant="outlined" onClick={handleProxyPreview} sx={{ mb: 1 }}>
                  Run preview
                </Button>
                {proxyPreviewResult && (
                  <Box sx={{ mt: 1, p: 1.25, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="caption" component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                      {JSON.stringify(proxyPreviewResult, null, 2)}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}


      {activeTab === 'faxtosign' && (
        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
          <Grid size={{ xs: 12 }}>
            <Card elevation={2}>
              <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
                <Typography variant="h6" sx={{ mb: 0.5 }}>Faxes to file</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Inbound file-only faxes (name + DOB, no sign-and-return) go to the Triage “Faxes filed” drawer, not the board. Rotation is per this login.
                </Typography>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Faxes filed rotation period</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                  How many days just-filed faxes stay on <strong>your</strong> Faxes filed list. After that they drop off this login only — the PDF stays on the patient chart. The hold queue does not rotate.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <TextField
                    type="number"
                    size="small"
                    label="Days"
                    value={faxFiledRotationDays}
                    onChange={(e) => setFaxFiledRotationDays(Math.max(1, Math.min(365, parseInt(e.target.value, 10) || 7)))}
                    inputProps={{ min: 1, max: 365 }}
                    sx={{ width: 120 }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSaveFaxFiledPrefs}
                    disabled={faxFiledSaving}
                    sx={{ textTransform: 'none', minHeight: 36 }}
                  >
                    {faxFiledSaving ? 'Saving…' : 'Save my list days'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Card elevation={2}>
              <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, sm: 1.5 }, flexWrap: 'wrap' }}>
                  <FaxTabIcon color="primary" />
                  <Typography variant="h6">Faxes to sign</Typography>
                  <InfoTip text="Inbound faxes from allowlisted senders (or matching “please sign / fax back” text) go to the Triage “Faxes to sign” drawer — not clinical Todo. Upload a practice digital signature for stamp-and-return. Auto-send only fires when the RETURN fax number (inside the PDF) is on the auto-send list AND that switch is on — inbound sender allowlist alone never auto-sends." />
                  <Box sx={{ flex: 1 }} />
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveFaxSignatures}
                    disabled={faxSignSaving}
                    sx={{ textTransform: 'none', minHeight: 36 }}
                  >
                    {faxSignSaving ? 'Saving…' : 'Save Fax Settings'}
                  </Button>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Save applies to routing switches and phone lists below — not the signature image (use Upload / Save drawn signature for that).
                </Typography>
                <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />

                <FormControlLabel
                  control={<Switch checked={faxSignEnabled} onChange={(_, c) => setFaxSignEnabled(c)} />}
                  label="Enable signature fax routing"
                  sx={{ mb: 1 }}
                />
                <FormControlLabel
                  control={<Switch checked={faxSignCues} onChange={(_, c) => setFaxSignCues(c)} />}
                  label='Also route faxes whose text looks like “please sign / fax back to…”'
                  sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Signature sender phones (route to Signatures queue)</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                  One per line — e.g. the (414) clearinghouse line that sends forms to sign. Return-fax number inside the PDF may differ; that is expected.
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  value={faxSignPhonesText}
                  onChange={(e) => setFaxSignPhonesText(e.target.value)}
                  placeholder={"(414) 389-6737"}
                  sx={{ mb: 2 }}
                />

                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Auto-send RETURN fax numbers</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                  These are numbers we may fax the signed PDF <strong>back to</strong> — not the inbound sender.
                  Auto-send only runs when this list matches the return number <em>and</em> the switch below is on (still experimental on dev).
                </Typography>
                <FormControlLabel
                  control={<Switch checked={faxAutoSendEnabled} onChange={(_, c) => setFaxAutoSendEnabled(c)} />}
                  label="Enable auto-send when return number is on this list"
                  sx={{ mb: 1 }}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  value={faxAutoSendPhonesText}
                  onChange={(e) => setFaxAutoSendPhonesText(e.target.value)}
                  placeholder={"(630) 368-6580"}
                  sx={{ mb: 2 }}
                />

                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Doctors' Digital Signature</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                  Upload a PNG/JPEG, or draw with finger / stylus / mouse. Saved signature is used to stamp “sign & return” faxes.
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: 360,
                    height: 96,
                    mb: 1.5,
                    border: '2px dashed',
                    borderColor: 'grey.300',
                    borderRadius: 2,
                    bgcolor: 'grey.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    px: 2,
                  }}
                >
                  {faxPracticeSigUrl ? (
                    <Box
                      component="img"
                      src={faxPracticeSigUrl}
                      alt="Practice signature"
                      onError={() => setFaxPracticeSigUrl(null)}
                      sx={{ maxWidth: '100%', maxHeight: 80, objectFit: 'contain' }}
                    />
                  ) : (
                    <Box sx={{ textAlign: 'center', color: 'grey.500', width: '100%' }}>
                      <Box
                        sx={{
                          mx: 'auto',
                          mb: 0.75,
                          width: '72%',
                          height: 22,
                          borderBottom: '1.5px solid',
                          borderColor: 'grey.400',
                          position: 'relative',
                          opacity: 0.85,
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: '10%',
                            right: '22%',
                            bottom: 6,
                            height: 16,
                            borderBottom: '2px solid',
                            borderColor: 'grey.500',
                            borderRadius: '40%',
                            transform: 'rotate(-8deg)',
                          },
                        }}
                      />
                      <Typography variant="caption" display="block">
                        No signature uploaded yet
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ mb: 1.5 }}>
                  <SignaturePad
                    ref={faxSigPadRef}
                    width={360}
                    height={140}
                    label="Or sign with finger, stylus or mouse"
                    onDirtyChange={setFaxSigPadHasInk}
                    disabled={faxSigPadSaving}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={!faxSigPadHasInk || faxSigPadSaving}
                    sx={{ textTransform: 'none', mt: 0.25 }}
                    onClick={async () => {
                      const blob = await faxSigPadRef.current?.toBlob();
                      if (!blob) return;
                      setFaxSigPadSaving(true);
                      try {
                        const file = new File([blob], `drawn-signature-${Date.now()}.png`, { type: 'image/png' });
                        const res = await faxSignaturesApi.uploadPracticeSignature(file);
                        setFaxPracticeSigUrl(res.data?.data?.practiceSignatureUrl || null);
                        faxSigPadRef.current?.clear();
                        setFaxSigPadHasInk(false);
                        setSuccessMessage('Drawn practice signature saved.');
                        setTimeout(() => setSuccessMessage(null), 3000);
                      } catch (err: any) {
                        setError(err?.response?.data?.error || 'Could not save drawn signature');
                      } finally {
                        setFaxSigPadSaving(false);
                      }
                    }}
                  >
                    {faxSigPadSaving ? 'Saving signature…' : 'Save drawn signature'}
                  </Button>
                </Box>

                {faxSuggestedPhones.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Suggested inbound senders (text-cue detections)</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                      AI/rules thought these inbound numbers look like “sign & fax back” sources. Review before adding to the route allowlist — not added automatically.
                    </Typography>
                    {faxSuggestedPhones.map((ph) => (
                      <Box key={ph} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="body2">{ph}</Typography>
                        <Button
                          size="small"
                          onClick={async () => {
                            await faxSignaturesApi.acceptSuggestion(ph);
                            await loadFaxSignatures();
                          }}
                        >
                          Add to allowlist
                        </Button>
                      </Box>
                    ))}
                  </Box>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    sx={{ textTransform: 'none', minHeight: 40 }}
                  >
                    {faxPracticeSigUrl ? 'Replace signature' : 'Upload signature'}
                    <input
                      hidden
                      type="file"
                      accept="image/png,image/jpeg"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const res = await faxSignaturesApi.uploadPracticeSignature(file);
                          setFaxPracticeSigUrl(res.data?.data?.practiceSignatureUrl || null);
                          setSuccessMessage('Practice signature uploaded.');
                          setTimeout(() => setSuccessMessage(null), 3000);
                        } catch (err: any) {
                          setError(err?.response?.data?.error || 'Signature upload failed');
                        } finally {
                          e.target.value = '';
                        }
                      }}
                    />
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {activeTab === 'branding' && (
        <Grid container spacing={2}>
          {/* Site Identity */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card elevation={2}>
              <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PaletteIcon color="primary" />
                  Site Identity
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Header Text"
                    value={brandingForm.headerText}
                    onChange={(e) => setBrandingForm(prev => ({ ...prev, headerText: e.target.value }))}
                    fullWidth
                    helperText="Displayed in the login page header and sidebar (e.g., MedicalAI)"
                  />
                  <TextField
                    label="Slogan Text"
                    value={brandingForm.sloganText}
                    onChange={(e) => setBrandingForm(prev => ({ ...prev, sloganText: e.target.value }))}
                    fullWidth
                    helperText="Displayed below the header on the login page (e.g., Healthcare Management Platform)"
                  />
                  <TextField
                    label="Copyright Text"
                    value={brandingForm.copyrightText}
                    onChange={(e) => setBrandingForm(prev => ({ ...prev, copyrightText: e.target.value }))}
                    fullWidth
                    multiline
                    rows={2}
                    helperText="Displayed in the login page footer"
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={isSavingBranding ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                      onClick={handleSaveBranding}
                      disabled={isSavingBranding}
                    >
                      {isSavingBranding ? 'Saving...' : 'Save Text Settings'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Logo Upload */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card elevation={2}>
              <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ImageIcon color="primary" />
                  Logo / Favicon
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  {/* Logo Preview */}
                  <Box
                    sx={{
                      width: 120,
                      height: 120,
                      border: '2px dashed',
                      borderColor: 'grey.300',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.50',
                      overflow: 'hidden',
                    }}
                  >
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo preview"
                        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                      />
                    ) : (
                      <Box sx={{ textAlign: 'center', color: 'grey.500' }}>
                        <ImageIcon sx={{ fontSize: 40 }} />
                        <Typography variant="caption" display="block">
                          No logo set
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Upload a logo to use on the login page, sidebar, and browser favicon.
                    <br />
                    Recommended: 512x512 PNG with transparent background.
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<CloudUploadIcon />}
                    >
                      Select File
                      <input
                        type="file"
                        hidden
                        accept="image/png,image/jpeg,image/svg+xml,image/x-icon"
                        onChange={handleLogoFileSelect}
                      />
                    </Button>

                    {logoFile && (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={isUploadingLogo ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />}
                        onClick={handleUploadLogo}
                        disabled={isUploadingLogo}
                      >
                        {isUploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                    )}

                    {branding.logoUrl && (
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={handleRemoveLogo}
                      >
                        Remove Logo
                      </Button>
                    )}
                  </Box>

                  {logoFile && (
                    <Typography variant="caption" color="text.secondary">
                      Selected: {logoFile.name}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Preview */}
          <Grid size={{ xs: 12 }}>
            <Card elevation={2}>
              <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Preview
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box 
                  sx={{ 
                    p: 3, 
                    bgcolor: 'primary.main', 
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: 'white',
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: 2,
                    }}
                  >
                    {logoPreview ? (
                      <img
                        src={logoPreview}
                        alt="Logo"
                        style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain' }}
                      />
                    ) : (
                      <ServiceIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                    )}
                  </Box>
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 700 }}>
                    {brandingForm.headerText || 'MedicalAI'}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    {brandingForm.sloganText || 'Healthcare Management Platform'}
                  </Typography>
                </Box>
                
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {brandingForm.copyrightText || '© 2026 Victor Levitan, MD, PLLC - All Rights Reserved.'}
                  </Typography>
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>Browser Title:</strong> {brandingForm.headerText || 'MedicalAI'} - {brandingForm.sloganText || 'Healthcare Management Platform'}
                </Alert>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* AI Training Tab Content */}
      {activeTab === 'aitraining' && (
        <Grid container spacing={2}>
          {/* AI Voice — practice default + optional module overrides */}
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, sm: 1.5 } }}>
                  <VoiceIcon color="primary" />
                  <Typography variant="h6">AI Voice</Typography>
                  <InfoTip text="Practice default sets the tone for AI drafts. Optionally override per module: Triage Dashboard (suggested replies), AI Consultation (Doctor) for consult chat, and AI Consultation (Patient Message) for drafts copied into patient messages. Every outbound draft is still reviewed before send." />
                </Box>
                <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />

                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Practice default</Typography>
                <ToggleButtonGroup
                  exclusive
                  value={aiVoicePreset}
                  onChange={(_, v) => {
                    if (!v) return;
                    setAiVoicePreset(v);
                  }}
                  size="small"
                  aria-label="AI voice preset"
                  sx={{
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 1.5,
                    '& .MuiToggleButton-root': { textTransform: 'none', borderRadius: 1, px: 1.5, border: '1px solid', borderColor: 'divider' },
                  }}
                >
                  {aiVoicePresets.map((p) => (
                    <ToggleButton key={p.key} value={p.key}>{p.label}</ToggleButton>
                  ))}
                  <ToggleButton value="custom">Custom</ToggleButton>
                </ToggleButtonGroup>

                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  {aiVoicePreset === 'custom'
                    ? 'Uses box 4 (Custom) in the Voice Skill Library below. Edit there — it auto-saves.'
                    : (aiVoicePresets.find((p) => p.key === aiVoicePreset)?.description || '')}
                </Typography>

                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Module overrides</Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Leave on “Use practice default” unless a module needs a different voice.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2 }}>
                  {(aiVoiceModules.length ? aiVoiceModules : [
                    { key: 'triage', label: 'Triage Dashboard', description: '' },
                    { key: 'consultation_doctor', label: 'AI Consultation (Doctor)', description: '' },
                    { key: 'consultation_patient', label: 'AI Consultation (Patient Message)', description: '' },
                  ]).map((m) => (
                    <Box
                      key={m.key}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 1,
                        py: 0.75,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600}>{m.label}</Typography>
                        {m.description && (
                          <Typography variant="caption" color="text.secondary">{m.description}</Typography>
                        )}
                      </Box>
                      <TextField
                        select
                        size="small"
                        label="Voice"
                        value={aiVoiceModulePick[m.key] || 'default'}
                        onChange={(e) =>
                          setAiVoiceModulePick((prev) => ({ ...prev, [m.key]: e.target.value }))
                        }
                        SelectProps={{ native: true }}
                        sx={{ minWidth: { xs: '100%', sm: 220 } }}
                      >
                        <option value="default">Use practice default</option>
                        {aiVoicePresets.map((p) => (
                          <option key={p.key} value={p.key}>{p.label}</option>
                        ))}
                        <option value="custom">Custom (Voice Skill Library box 4)</option>
                      </TextField>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveAiVoice}
                    disabled={aiVoiceSaving}
                    startIcon={aiVoiceSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
                  >
                    {aiVoiceSaving ? 'Saving…' : 'Save AI Voice'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Voice Skill Library — local how-to-write texts (not pushed to Chatbase) */}
          <Grid size={{ xs: 12 }}>
            <Card elevation={2}>
              <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, sm: 1.5 }, flexWrap: 'wrap' }}>
                  <PsychologyIcon color="primary" />
                  <Typography variant="h6">Voice Skill Library</Typography>
                  <InfoTip text="Editable how-to-write skills for AI drafts. Module overrides pick which box is active. On save, Practice Pilot updates the matching VOICE SKILL (AUTO) blocks in Chatbase Playground (triage + consult agents) — the rest of those prompts is left alone. Keep replies as plain text (no Markdown asterisks)." />
                  <Box sx={{ flexGrow: 1 }} />
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 140, textAlign: 'right' }}>
                    {aiVoiceLibrarySaving
                      ? 'Saving…'
                      : aiVoiceLibrarySavedAt
                        ? `Saved ${new Date(aiVoiceLibrarySavedAt).toLocaleTimeString()}`
                        : 'Auto-saves as you type'}
                  </Typography>
                </Box>
                <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />

                <Grid container spacing={1.5}>
                  {VOICE_LIBRARY_CARDS.map((card) => (
                    <Grid key={card.key} size={{ xs: 12, md: 6 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {card.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                        {card.hint}
                      </Typography>
                      <TextField
                        multiline
                        minRows={isMobile ? 5 : 7}
                        maxRows={isMobile ? 5 : 7}
                        fullWidth
                        size="small"
                        value={aiVoiceLibrary[card.key] || ''}
                        onChange={(e) => handleAiVoiceLibraryChange(card.key, e.target.value)}
                        placeholder="How the AI should write when this voice is selected…"
                        inputProps={{ spellCheck: false }}
                        sx={{
                          '& .MuiInputBase-root': { alignItems: 'flex-start' },
                          '& .MuiInputBase-input': {
                            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                            fontSize: '0.8rem',
                            overflow: 'auto !important',
                          },
                          ...(aiVoicePreset === card.key || (aiVoicePreset === 'custom' && card.key === 'custom')
                            ? { '& .MuiOutlinedInput-root': { borderColor: 'primary.main' } }
                            : {}),
                        }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Clinic Knowledge Documents */}
          <Grid size={{ xs: 12 }}>
            <Card elevation={2}>
              <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, sm: 1.5 } }}>
                  <CloudUploadIcon color="primary" />
                  <Typography variant="h6">Clinic Knowledge Documents</Typography>
                  <InfoTip text="Upload practice facts (hours, policies, referral rules). Separate from Voice Skill Library tone. Stored for clinic knowledge — not used as a voice/style knob." />
                </Box>
                <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />

                <input
                  ref={trainingFileInputRef}
                  type="file"
                  accept=".txt,.md,text/plain"
                  style={{ display: 'none' }}
                  onChange={(e) => handleTrainingFiles(e.target.files)}
                />
                <Box
                  role="button"
                  tabIndex={0}
                  aria-label="Upload a clinic knowledge text file"
                  onClick={() => trainingFileInputRef.current?.click()}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') trainingFileInputRef.current?.click(); }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); handleTrainingFiles(e.dataTransfer.files); }}
                  sx={{
                    border: '2px dashed',
                    borderColor: 'grey.300',
                    borderRadius: 2,
                    p: { xs: 2, sm: 3 },
                    textAlign: 'center',
                    bgcolor: 'grey.50',
                    cursor: 'pointer',
                    '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                    '&:focus-visible': { outline: (t) => `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: { xs: 32, sm: 44 }, color: 'grey.400', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    {isUploadingTraining ? 'Uploading…' : isMobile ? 'Tap to add clinic knowledge (.txt)' : 'Drop clinic knowledge (.txt), or click to choose'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Practice facts · not voice tone · .txt / .md
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Compression Skills — skill store aggressiveness knobs */}
          <Grid size={{ xs: 12 }}>
            <Card elevation={2}>
              <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, sm: 1.5 } }}>
                  <TuneIcon color="primary" />
                  <Typography variant="h6">Compression Skills</Typography>
                  <InfoTip text="These skills shorten what the AI sees (med lists, message history, inbound Spruce feed). They do not change the patient chart UI. Rule-based today — model-agnostic for the future skill store. Off / Light / Standard / Aggressive." />
                </Box>
                <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />
                {aiSkills.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No skills loaded.</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {aiSkills.map((skill) => (
                      <Box
                        key={skill.key}
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'stretch', sm: 'center' },
                          justifyContent: 'space-between',
                          gap: 1,
                          py: 1,
                          borderTop: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" fontWeight={600}>{skill.name}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {skill.key} · {skill.taskType} · {skill.engine}
                          </Typography>
                          {skill.description && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                              {skill.description}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={!!skill.enabled}
                                disabled={aiSkillsSaving === skill.key}
                                onChange={(_, checked) => handleSaveAiSkill(skill.key, { enabled: checked })}
                              />
                            }
                            label={<Typography variant="caption">On</Typography>}
                          />
                          <TextField
                            select
                            size="small"
                            label="Level"
                            value={skill.aggressiveness || 'standard'}
                            disabled={!skill.enabled || aiSkillsSaving === skill.key}
                            onChange={(e) => handleSaveAiSkill(skill.key, { aggressiveness: e.target.value })}
                            SelectProps={{ native: true }}
                            sx={{ minWidth: 140 }}
                          >
                            <option value="off">Off</option>
                            <option value="light">Light</option>
                            <option value="standard">Standard</option>
                            <option value="aggressive">Aggressive</option>
                          </TextField>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* AI Automation — set-once behavior toggle (moved from Settings) */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ border: settings.appointment_triggers_enabled ? '2px solid #ff9800' : '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, sm: 1.5 } }}>
                  <AutomationIcon color={settings.appointment_triggers_enabled ? "warning" : "primary"} />
                  <Typography variant="h6">AI Automation</Typography>
                  <InfoTip text="When enabled, the AI detects scheduling requests and drafts responses that must be approved by a human before sending." />
                  {settings.appointment_triggers_enabled && (
                    <Chip label="ACTIVE" color="warning" size="small" sx={{ ml: 'auto' }} />
                  )}
                </Box>
                <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />

                <FormControlLabel
                  sx={{ mb: 0.5 }}
                  control={
                    <Switch
                      checked={settings.appointment_triggers_enabled}
                      onChange={(e) => setSettings(prev => ({ ...prev, appointment_triggers_enabled: e.target.checked }))}
                      color="warning"
                    />
                  }
                  label={
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Enable AI Scheduling Drafts (Human Review)
                    </Typography>
                  }
                />

                <Typography variant="caption" color="text.secondary" display="block">
                  <strong>Triggers on:</strong> appointment, schedule, book, urgent, asap
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Pleasantries Auto-Response — set-once behavior toggle (moved from Settings) */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card sx={{ border: settings.pleasantries_auto_response_enabled ? '2px solid #f44336' : '1px solid #e0e0e0' }}>
              <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, '&:last-child': { pb: { xs: 1.25, sm: 1.5 } } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 1, sm: 1.5 } }}>
                  <PleasantriesIcon color={settings.pleasantries_auto_response_enabled ? "error" : "primary"} />
                  <Typography variant="h6">Pleasantries Auto-Response</Typography>
                  <InfoTip text='When enabled, simple messages like "Thank you!", "OK", "Goodbye" get an automatic reply without staff review. Everything else still goes through a human.' />
                  {settings.pleasantries_auto_response_enabled && (
                    <Chip label="ACTIVE" color="error" size="small" sx={{ ml: 'auto' }} />
                  )}
                </Box>
                <Divider sx={{ mb: { xs: 1.5, sm: 2 } }} />

                {/* The only auto-send in the product — keep a hard warning while on */}
                {settings.pleasantries_auto_response_enabled && (
                  <Alert severity="error" sx={{ mb: 1.5, py: 0 }}>
                    Replies to pleasantries send without human review.
                  </Alert>
                )}

                <FormControlLabel
                  sx={{ mb: 0.5 }}
                  control={
                    <Switch
                      checked={settings.pleasantries_auto_response_enabled}
                      onChange={(e) => setSettings(prev => ({ ...prev, pleasantries_auto_response_enabled: e.target.checked }))}
                      color="error"
                    />
                  }
                  label={
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Enable Pleasantries Auto-Response
                    </Typography>
                  }
                />

                <Typography variant="caption" color="text.secondary" display="block">
                  <strong>Triggers on:</strong> thank you, thanks, ok, okay, got it, goodbye, bye
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Save — persists the AI Automation + Pleasantries toggles */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleSaveSettings}
                disabled={isSavingSettings}
              >
                {isSavingSettings ? 'Saving…' : 'Save Automation Settings'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      )}

      {/* Enhanced Specialist Modal with Working Hours */}
      <Dialog
        open={isSpecialistModalOpen}
        onClose={() => setIsSpecialistModalOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          {editingSpecialist ? 'Edit Provider' : 'Add Provider'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {/* Basic Info */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Basic Information
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="Name"
                    value={specialistForm.name}
                    onChange={(e) => setSpecialistForm(prev => ({ ...prev, name: e.target.value }))}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="Phone"
                    value={specialistForm.phone}
                    onChange={(e) => setSpecialistForm(prev => ({ ...prev, phone: e.target.value }))}
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    label="Email"
                    value={specialistForm.email}
                    onChange={(e) => setSpecialistForm(prev => ({ ...prev, email: e.target.value }))}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <FormControlLabel
                control={
                  <Switch
                    checked={specialistForm.isActive}
                    onChange={(e) => setSpecialistForm(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                }
                label="Active"
                sx={{ mt: 1 }}
              />
            </Box>

            <Divider />

            {/* Working Hours */}
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ScheduleIcon fontSize="small" />
                Working Hours
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Toggle a day on to set its hours. Add a block for a split shift (e.g. a lunch break).
              </Typography>

              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  px: { xs: 1, sm: 1.5 },
                }}
              >
                {DAYS_OF_WEEK.map((day) => {
                  const dayKey = day.key as keyof WorkingHours;
                  const dayBlocks = specialistForm.workingHours[dayKey] || [];
                  const isWorking = dayBlocks.length > 0;

                  return (
                    <Box
                      key={day.key}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'stretch', sm: 'center' },
                        gap: { xs: 0.5, sm: 1.5 },
                        py: 1,
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                        '&:last-of-type': { borderBottom: 'none' },
                      }}
                    >
                      <FormControlLabel
                        sx={{ m: 0, width: { sm: 132 }, flexShrink: 0 }}
                        control={
                          <Switch
                            checked={isWorking}
                            onChange={() => handleToggleDay(dayKey)}
                            size="small"
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: isWorking ? 'text.primary' : 'text.secondary' }}
                          >
                            {day.label}
                          </Typography>
                        }
                      />

                      {isWorking ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, pl: { xs: 5.5, sm: 0 } }}>
                          {dayBlocks.map((block, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <TextField
                                type="time"
                                size="small"
                                value={block.start}
                                onChange={(e) => handleUpdateTimeBlock(dayKey, idx, 'start', e.target.value)}
                                sx={{ width: 132 }}
                                InputLabelProps={{ shrink: true }}
                              />
                              <Typography variant="body2" color="text.secondary">–</Typography>
                              <TextField
                                type="time"
                                size="small"
                                value={block.end}
                                onChange={(e) => handleUpdateTimeBlock(dayKey, idx, 'end', e.target.value)}
                                sx={{ width: 132 }}
                                InputLabelProps={{ shrink: true }}
                              />
                              {dayBlocks.length > 1 && (
                                <Tooltip title="Remove block">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleRemoveTimeBlock(dayKey, idx)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          ))}
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleAddTimeBlock(dayKey)}
                            sx={{ minWidth: 0 }}
                          >
                            Block
                          </Button>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled" sx={{ pl: { xs: 5.5, sm: 0 } }}>
                          Off
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsSpecialistModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSpecialist} disabled={!specialistForm.name}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Service Modal */}
      <Dialog open={isServiceModalOpen} onClose={() => setIsServiceModalOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {editingService ? 'Edit Service' : 'Add Service'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Service Name"
              value={serviceForm.name}
              onChange={(e) => setServiceForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Duration (minutes)"
              type="number"
              value={serviceForm.duration}
              onChange={(e) => setServiceForm(prev => ({ ...prev, duration: parseInt(e.target.value) || 30 }))}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={serviceForm.isActive}
                  onChange={(e) => setServiceForm(prev => ({ ...prev, isActive: e.target.checked }))}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsServiceModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveService} disabled={!serviceForm.name}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mapping Modal */}
      <Dialog open={isMappingModalOpen} onClose={() => setIsMappingModalOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>Add Provider-Service Mapping</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label="Provider"
              value={mappingForm.specialistId}
              onChange={(e) => setMappingForm(prev => ({ ...prev, specialistId: e.target.value }))}
              fullWidth
              SelectProps={{ native: true }}
            >
              <option value="">Select Provider</option>
              {specialists.map((specialist) => (
                <option key={specialist.id} value={specialist.id}>
                  {specialist.name}
                </option>
              ))}
            </TextField>
            <TextField
              select
              label="Service"
              value={mappingForm.serviceId}
              onChange={(e) => setMappingForm(prev => ({ ...prev, serviceId: e.target.value }))}
              fullWidth
              SelectProps={{ native: true }}
            >
              <option value="">Select Service</option>
              {services.map((service) => (
                <option key={service.id} value={service.id}>
                  {service.name} ({service.duration} mins)
                </option>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsMappingModalOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveMapping}
            disabled={!mappingForm.serviceId || !mappingForm.specialistId}
          >
            Add Mapping
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lab Modal */}
      <Dialog open={isLabModalOpen} onClose={() => setIsLabModalOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          {editingLab ? 'Edit Lab' : 'Add Lab'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Lab Name"
              value={labForm.name}
              onChange={(e) => setLabForm((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
              required
            />
            <TextField
              label="Category (optional)"
              value={labForm.category}
              onChange={(e) => setLabForm((prev) => ({ ...prev, category: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Phone/Fax Numbers (comma-separated)"
              value={labForm.phoneNumbers}
              onChange={(e) => setLabForm((prev) => ({ ...prev, phoneNumbers: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Aliases (comma-separated)"
              value={labForm.aliases}
              onChange={(e) => setLabForm((prev) => ({ ...prev, aliases: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Notes"
              value={labForm.notes}
              onChange={(e) => setLabForm((prev) => ({ ...prev, notes: e.target.value }))}
              fullWidth
              multiline
              rows={2}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={labForm.status !== 'inactive'}
                  onChange={(e) => setLabForm((prev) => ({ ...prev, status: e.target.checked ? 'active' : 'inactive' }))}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsLabModalOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveLab} disabled={!labForm.name.trim() || isSavingLab}>
            {isSavingLab ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SMS Template Preview Modal */}
      <Dialog open={isTemplateModalOpen} onClose={() => setIsTemplateModalOpen(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          📱 SMS Templates Preview
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {Object.entries(smsTemplates).map(([key, template]) => (
              <Paper key={key} variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'primary.main' }}>
                  {template.name}
                </Typography>
                <Box 
                  sx={{ 
                    bgcolor: 'grey.100', 
                    p: 2, 
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                    lineHeight: 1.6,
                  }}
                >
                  {template.template}
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Variables: {template.variables.map(v => `{${v}}`).join(', ')}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsTemplateModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(pendingConfirm)}
        title={pendingConfirm?.title || ''}
        body={pendingConfirm?.body}
        confirmLabel={pendingConfirm?.confirmLabel || 'Confirm'}
        busy={confirmBusy}
        onConfirm={runPendingConfirm}
        onClose={() => setPendingConfirm(null)}
      />
    </Box>
  );
};

export default Admin;
